import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import SatelliteProgressPlan from "./SatelliteProgressPlan";
import MeasurementSheet from "./MeasurementSheet";
import KmzRouteImporter from "./KmzRouteImporter";
import {
  parseKmlFile,
  deriveAssetGeometry,
  assetFieldsFromGeometry,
} from "./kmlGeometry";
import ProductivityView from "./ProductivityView";
import { tokens } from "./theme";
import {
  computeTestStatus,
  TEST_TYPES,
  TEST_TYPE_META,
  RESULT_COLORS,
  testLocationLabel,
  positionRelativeCompaction,
  cubeAverage28,
} from "./QualityTestsSection";

const C = tokens.color;

/* ------------------------------------------------------------------ */
/* Asset type library — drives form fields, icons and 2.5D / 3D model */
/* ------------------------------------------------------------------ */

export const ASSET_TYPES = {
  pipeline: {
    label: "Pipeline",
    icon: "🛢️",
    accent: "#0ea5e9",
    measurement: "chainage", // start/end chainage in metres
    description: "Linear asset measured by chainage along a route.",
    activities: [
      "tmp", "site_clearance", "tar_cutting", "tar_removing", "excavation",
      "dump_rock", "crusher", "bedding", "pipe_laying", "concrete_encasement",
      "welding", "culverts", "backfill", "testing", "reinstatement",
      "services_adjoining", "services_crossing",
    ],
  },
  road: {
    label: "Road / Pavement",
    icon: "🛣️",
    accent: "#64748b",
    measurement: "chainage",
    description: "Linear pavement asset measured by chainage.",
    activities: [
      "tmp", "site_clearance", "earthworks", "subbase", "basecourse",
      "asphalt_binder", "asphalt_wearing", "kerbing", "linemarking",
    ],
  },
  pump_station: {
    label: "Pump Station",
    icon: "⚙️",
    accent: "#8b5cf6",
    measurement: "components",
    description: "Component-based asset tracked by stage of completion.",
    activities: [
      "civils", "wet_well", "mechanical", "electrical",
      "controls", "commissioning", "testing",
    ],
  },
  building: {
    label: "Building",
    icon: "🏗️",
    accent: "#f59e0b",
    measurement: "floors",
    description: "Vertical asset tracked by floor / trade.",
    activities: [
      "foundations", "slab", "superstructure", "roofing",
      "facade", "internal_walls", "mep", "finishes",
    ],
  },
  earthworks: {
    label: "Earthworks / Cut & Fill",
    icon: "⛰️",
    accent: "#a16207",
    measurement: "volume",
    description: "Volumetric asset tracked by m³ moved.",
    activities: [
      "strip_topsoil", "cut", "haul", "fill",
      "compaction", "trim_to_level",
    ],
  },
};

export const ACTIVITY_LABELS = {
  // pipeline
  tmp: "Traffic Management (TMP)",
  site_clearance: "Site Clearance",
  tar_cutting: "Tar Cutting",
  tar_removing: "Tar Removing",
  excavation: "Excavation",
  dump_rock: "Dump Rock",
  crusher: "Crusher",
  bedding: "Bedding",
  pipe_laying: "Pipe Laying",
  concrete_encasement: "Concrete Encasement",
  welding: "Welding",
  culverts: "Culverts",
  backfill: "Backfill",
  testing: "Testing",
  reinstatement: "Reinstatement",
  services_adjoining: "Services (Adjoining)",
  services_crossing: "Services (Crossing)",
  // road
  earthworks: "Earthworks",
  subbase: "Subbase",
  basecourse: "Basecourse",
  asphalt_binder: "Asphalt Binder",
  asphalt_wearing: "Asphalt Wearing",
  kerbing: "Kerbing",
  linemarking: "Line Marking",
  // pump station
  civils: "Civils",
  wet_well: "Wet Well",
  mechanical: "Mechanical",
  electrical: "Electrical",
  controls: "Controls",
  commissioning: "Commissioning",
  // building
  foundations: "Foundations",
  slab: "Slab",
  superstructure: "Superstructure",
  roofing: "Roofing",
  facade: "Façade",
  internal_walls: "Internal Walls",
  mep: "MEP",
  finishes: "Finishes",
  // earthworks
  strip_topsoil: "Strip Topsoil",
  cut: "Cut",
  haul: "Haul",
  fill: "Fill",
  compaction: "Compaction",
  trim_to_level: "Trim to Level",
};

/* ------------------------------------------------------------------ */
/* Existing services encountered along the route                       */
/* "Adjoining" services run parallel to the pipeline over a chainage   */
/* range; "crossing" services cut across it at a single stake value    */
/* and are drawn as a circle sized on the service diameter.            */
/* ------------------------------------------------------------------ */

export const SERVICE_ACTIVITIES = ["services_adjoining", "services_crossing"];

export const isServiceActivity = (activity) => SERVICE_ACTIVITIES.includes(activity);

export const SERVICE_TYPES = [
  { key: "electrical",   label: "Electrical",   colour: "#ef4444", soft: "#fee2e2" },
  { key: "gas",          label: "Gas",          colour: "#f59e0b", soft: "#fef3c7" },
  { key: "sewer",        label: "Sewer",        colour: "#78350f", soft: "#f5ede4" },
  { key: "stormwater",   label: "Stormwater",   colour: "#0ea5e9", soft: "#e0f2fe" },
  { key: "reticulation", label: "Reticulation", colour: "#2563eb", soft: "#dbeafe" },
  { key: "bulk",         label: "Bulk",         colour: "#7c3aed", soft: "#ede9fe" },
];

export const SERVICE_TYPE_BY_KEY = Object.fromEntries(SERVICE_TYPES.map((s) => [s.key, s]));

export const UNKNOWN_SERVICE = {
  key: "unknown",
  label: "Unspecified",
  colour: "#94a3b8",
  soft: "#f1f5f9",
};

export const serviceMeta = (key) => SERVICE_TYPE_BY_KEY[key] || UNKNOWN_SERVICE;

export const STATUS_COLOURS = {
  completed: "#22c55e",
  in_progress: "#f59e0b",
  delayed: "#ef4444",
  rework: "#8b5cf6",
  not_started: "#d1d5db",
};

/* ------------------------------------------------------------------ */
/* Trench cross-section model                                          */
/* The vertical band order from surface (top) down to the invert.      */
/* Concrete encasement and culverts are NOT bands — they are discrete  */
/* structures on the pipe, so each gets its own row in a structures    */
/* lane under the trench rather than overlaying the pipe-laying band.  */
/* Site clearance / tar cutting / tar removing are not trench layers   */
/* either: they happen on the road surface, so they draw in their own  */
/* lane above the surface strip.                                       */
/* ------------------------------------------------------------------ */

export const TRENCH_LAYERS = [
  { key: "reinstatement", label: "Reinstatement", fill: "#d9c9a8", capStrip: "#6b7280" },
  { key: "backfill",      label: "Backfill",      fill: "#e1d2af" },
  { key: "bedding",       label: "Bedding",       fill: "#efe3c5" },
  { key: "pipe_laying",   label: "Pipe Laying",   fill: "#f4efe2", isPipeZone: true },
  { key: "welding",       label: "Welding",       fill: "#f4efe2", isPipeZone: true },
  { key: "testing",       label: "Testing",       fill: "#f4efe2", isPipeZone: true },
  { key: "crusher",       label: "Crusher",       fill: "#d6d9de", dotted: true },
  { key: "dump_rock",     label: "Dump Rock",     fill: "#c8cdd5", dotted: true },
  { key: "excavation",    label: "Excavation",    fill: "#b08a52" },
];

// Concrete encasement renders as a grey dashed bar on its own row.
export const ENCASEMENT_FILL = "rgba(100,116,139,0.42)";
export const ENCASEMENT_STROKE = "#475569";

// Culverts are box structures the pipeline runs through — a hatched teal bar,
// deliberately distinct from the grey encasement.
export const CULVERT_FILL = "rgba(13,148,136,0.30)";
export const CULVERT_STROKE = "#0f766e";

/* Structures carried on the pipe. They are recorded against chainage like any
   other progress segment, but they are not trench layers — stacking them over
   the pipe-laying band made both unreadable, so each gets its own row in a
   structures lane directly under the trench, with a dashed tie line back down
   to the pipe zone so the physical relationship is still legible. */
export const STRUCTURE_ACTIVITIES = [
  {
    key: "concrete_encasement",
    label: "Concrete Encasement",
    fill: ENCASEMENT_FILL,
    stroke: ENCASEMENT_STROKE,
    dashed: true,
    rx: 3,
  },
  {
    key: "culverts",
    label: "Culverts",
    fill: CULVERT_FILL,
    stroke: CULVERT_STROKE,
    hatch: "url(#vptCulvertHatch)",
    rx: 0,
  },
];

export const STRUCTURE_ACTIVITY_KEYS = STRUCTURE_ACTIVITIES.map((a) => a.key);

export const isStructureActivity = (activity) => STRUCTURE_ACTIVITY_KEYS.includes(activity);

/* Surface works — recorded against chainage but carried out on the road
   surface, not inside the trench. Each gets a row above the surface strip,
   coloured by status like any other progress segment. */
export const SURFACE_ACTIVITIES = [
  { key: "site_clearance", label: "Site Clearance" },
  { key: "tar_cutting",    label: "Tar Cutting" },
  { key: "tar_removing",   label: "Tar Removing" },
];

export const SURFACE_ACTIVITY_KEYS = SURFACE_ACTIVITIES.map((a) => a.key);

export const isSurfaceActivity = (activity) => SURFACE_ACTIVITY_KEYS.includes(activity);

/* ------------------------------------------------------------------ */
/* Traffic management (TMP)                                            */
/* Recorded like an adjoining service — a chainage range with a start   */
/* and a finish — but it is accommodation of traffic, not a buried      */
/* service, so it gets its own lane at the very top of the drawing.     */
/* ------------------------------------------------------------------ */

export const TMP_ACTIVITY = "tmp";

export const isTmpActivity = (activity) => activity === TMP_ACTIVITY;

export const TMP_COLOURS = {
  bar: "#f97316",
  soft: "#ffedd5",
  stroke: "#c2410c",
};

/* ------------------------------------------------------------------ */
/* Site clearance sub-sections                                         */
/* Clearance is measured and paid in two distinct parts, so a clearance */
/* segment carries a sub-type and draws on its own row.                 */
/* ------------------------------------------------------------------ */

export const CLEARANCE_SUBTYPES = [
  { key: "top_soil", label: "Top Soil", prefix: "(a)", colour: "#a16207", soft: "#fef3c7" },
  { key: "premix",   label: "Premix",   prefix: "(b)", colour: "#334155", soft: "#e2e8f0" },
];

export const CLEARANCE_SUBTYPE_BY_KEY = Object.fromEntries(
  CLEARANCE_SUBTYPES.map((s) => [s.key, s])
);

export const clearanceSubTypeMeta = (key) =>
  CLEARANCE_SUBTYPE_BY_KEY[key] || { key: "", label: "Unspecified", prefix: "", colour: "#94a3b8", soft: "#f1f5f9" };

/* ------------------------------------------------------------------ */
/* Excavation conditions                                               */
/* Whether a run was dug in wet or dry material changes both the rate   */
/* and the measurement items that apply to it, so it is recorded on the */
/* segment and shown on the excavation band.                            */
/* ------------------------------------------------------------------ */

export const EXCAVATION_CONDITIONS = [
  { key: "dry", label: "Dry Excavation", icon: "\u2600\ufe0f", colour: "#b45309", soft: "#fef3c7" },
  { key: "wet", label: "Wet Excavation", icon: "\ud83d\udca7", colour: "#0369a1", soft: "#e0f2fe" },
];

export const EXCAVATION_CONDITION_BY_KEY = Object.fromEntries(
  EXCAVATION_CONDITIONS.map((c) => [c.key, c])
);

export const excavationConditionMeta = (key) =>
  EXCAVATION_CONDITION_BY_KEY[key] || { key: "", label: "Condition not recorded", icon: "", colour: "#94a3b8", soft: "#f1f5f9" };

/* ------------------------------------------------------------------ */
/* Activity filters                                                    */
/* Some activities split further than their key: excavation splits on   */
/* wet/dry, site clearance on top soil/premix. A filter value of         */
/* "excavation@wet" means the excavation layer narrowed to wet runs.     */
/* ------------------------------------------------------------------ */

/** The sub-key a segment carries, if its activity has one. */
export function segmentSubKey(seg) {
  if (!seg) return "";
  if (seg.activity === "excavation") return seg.excavationCondition || "";
  if (seg.activity === "site_clearance") return seg.clearanceSubType || "";
  return "";
}

export function parseActivityFilter(value) {
  const [activity, sub = ""] = String(value || "").split("@");
  return { activity, sub };
}

export function segmentMatchesFilter(seg, value) {
  if (!value || value === "all") return true;
  const { activity, sub } = parseActivityFilter(value);
  if (seg?.activity !== activity) return false;
  if (sub && segmentSubKey(seg) !== sub) return false;
  return true;
}

/** Label for an activity filter value, including its sub-key where present. */
export function activityFilterLabel(value) {
  const { activity, sub } = parseActivityFilter(value);
  const base = ACTIVITY_LABELS[activity] || activity;
  if (!sub) return base;
  if (activity === "excavation") return excavationConditionMeta(sub).label;
  if (activity === "site_clearance") {
    const meta = clearanceSubTypeMeta(sub);
    return `${base} \u2014 ${meta.label}`;
  }
  return `${base} \u2014 ${sub}`;
}

/** Every filter option offered for an asset type's activity list. */
export function activityFilterOptions(activities = []) {
  const out = [];
  activities.forEach((a) => {
    out.push({ value: a, label: ACTIVITY_LABELS[a] || a });
    if (a === "excavation") {
      EXCAVATION_CONDITIONS.forEach((c) =>
        out.push({ value: `excavation@${c.key}`, label: `\u00a0\u00a0\u21b3 ${c.label}`, isSub: true })
      );
    }
    if (a === "site_clearance") {
      CLEARANCE_SUBTYPES.forEach((c) =>
        out.push({ value: `site_clearance@${c.key}`, label: `\u00a0\u00a0\u21b3 ${c.prefix} ${c.label}`, isSub: true })
      );
    }
  });
  return out;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export function formatChainage(value) {
  const n = Number(value || 0);
  return `CH ${n.toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
}

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function mergeIntervals(intervals) {
  const sorted = intervals
    .filter((item) => Number.isFinite(item.start) && Number.isFinite(item.end) && item.end > item.start)
    .sort((a, b) => a.start - b.start);

  const merged = [];
  sorted.forEach((item) => {
    const last = merged[merged.length - 1];
    if (!last || item.start > last.end) {
      merged.push({ ...item });
      return;
    }
    last.end = Math.max(last.end, item.end);
  });
  return merged;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function VisualProgressTracker({ selectedProject }) {
  const projectId = selectedProject?.id || selectedProject?.projectId || "";

  const [assets, setAssets] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tests, setTests] = useState([]);
  const [diaries, setDiaries] = useState([]);

  const [assetType, setAssetType] = useState("pipeline");
  const [assetName, setAssetName] = useState("Main Pipeline");
  const [startChainage, setStartChainage] = useState(0);
  const [endChainage, setEndChainage] = useState(3250);
  const [trenchWidthM, setTrenchWidthM] = useState("");
  const [trenchDepthM, setTrenchDepthM] = useState("");

  // The KMZ is the asset's geography: it supplies the route (and therefore the
  // length of a chainage asset) or, for a building, the footprint corners.
  const [kmzFile, setKmzFile] = useState(null);
  const [kmzGeometry, setKmzGeometry] = useState(null);
  const [kmzBusy, setKmzBusy] = useState(false);
  const [kmzError, setKmzError] = useState("");
  const kmzInputRef = useRef(null);

  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("all");
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [openDiary, setOpenDiary] = useState(null);
  const [chainageSearch, setChainageSearch] = useState("");

  const [presentationOpen, setPresentationOpen] = useState(false);
  const [kmzImporterOpen, setKmzImporterOpen] = useState(false);
  const [hoveredSegmentId, setHoveredSegmentId] = useState(null);
  const [measureSegment, setMeasureSegment] = useState(null);
  const [measurements, setMeasurements] = useState([]);

  // ── Quality-test overlay controls ──────────────────────────────────────────
  const [showTests, setShowTests] = useState(true);
  const [testTypeFilter, setTestTypeFilter] = useState("all");
  const [openTest, setOpenTest] = useState(null);
  const [coverageType, setCoverageType] = useState("compaction");
  const [maxSpacing, setMaxSpacing] = useState(100);

  /* ----- Firestore subscriptions ----- */

  useEffect(() => {
    if (!projectId) return;

    const qAssets = collection(db, "projects", projectId, "progressAssets");
    const unsub = onSnapshot(qAssets, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAssets(rows);
      if (!selectedAssetId && rows.length > 0) setSelectedAssetId(rows[0].id);
    });
    return () => unsub();
  }, [projectId, selectedAssetId]);

  useEffect(() => {
    if (!projectId) return;

    const qSegments = query(
      collection(db, "projects", projectId, "progressSegments"),
      orderBy("chainageStart", "asc")
    );
    const unsub = onSnapshot(qSegments, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSegments(rows);
    });
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    const unsub = onSnapshot(
      collection(db, "projects", projectId, "qualityTests"),
      (snap) => {
        setTests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
    return () => unsub();
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    const unsub = onSnapshot(
      collection(db, "projects", projectId, "diaries"),
      (snap) => {
        setDiaries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
    return () => unsub();
  }, [projectId]);

  // Daily measurements taken against this project's recorded sections. The
  // period summary sheet in Productivity rolls these up.
  useEffect(() => {
    if (!projectId) return;

    const unsub = onSnapshot(
      collection(db, "projects", projectId, "progressMeasurements"),
      (snap) => {
        setMeasurements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    );
    return () => unsub();
  }, [projectId]);

  /* ----- Derived state ----- */

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) || assets[0] || null,
    [assets, selectedAssetId]
  );

  // Lookup for ProductivityView — diary docs keyed by id.
  const diariesById = useMemo(() => {
    const map = {};
    diaries.forEach((d) => { map[d.id] = d; });
    return map;
  }, [diaries]);

  const selectedTypeMeta = ASSET_TYPES[selectedAsset?.assetType || "pipeline"] || ASSET_TYPES.pipeline;

  const assetStart = Number(selectedAsset?.startChainage ?? 0);
  const assetEnd = Number(selectedAsset?.endChainage ?? 0);
  const assetLength = Math.max(assetEnd - assetStart, 1);

  // All segments for the selected asset (ignores the activity filter) — the
  // trench view always draws every layer band and the encasement overlay.
  const assetSegments = useMemo(() => {
    return segments.filter((seg) => !selectedAsset || seg.assetId === selectedAsset.id);
  }, [segments, selectedAsset]);

  const assetMeasurements = useMemo(
    () => measurements.filter((m) => !selectedAsset || m.assetId === selectedAsset.id),
    [measurements, selectedAsset]
  );

  // Section id → number of measurement lines, so the trench can badge the
  // sections that have already been measured.
  const measurementCountBySegment = useMemo(() => {
    const map = {};
    measurements.forEach((m) => {
      if (!m.segmentId) return;
      map[m.segmentId] = (map[m.segmentId] || 0) + 1;
    });
    return map;
  }, [measurements]);

  const filteredSegments = useMemo(() => {
    return segments.filter((seg) => {
      if (selectedAsset && seg.assetId !== selectedAsset.id) return false;
      return segmentMatchesFilter(seg, selectedActivity);
    });
  }, [segments, selectedAsset, selectedActivity]);

  const progressPercentage = useMemo(() => {
    if (!selectedAsset) return null;
    if (selectedActivity === "all") return null;
    // Existing services are records of what is in the ground, not work done —
    // a "% complete" for them would be meaningless.
    if (isServiceActivity(parseActivityFilter(selectedActivity).activity)) return null;

    const completedIntervals = filteredSegments
      .filter((seg) => seg.status === "completed")
      .map((seg) => ({
        start: clamp(Number(seg.chainageStart), assetStart, assetEnd),
        end: clamp(Number(seg.chainageEnd), assetStart, assetEnd),
      }));

    const completed = mergeIntervals(completedIntervals).reduce(
      (sum, item) => sum + Math.max(item.end - item.start, 0),
      0
    );
    return clamp((completed / assetLength) * 100, 0, 100);
  }, [filteredSegments, selectedAsset, selectedActivity, assetStart, assetEnd, assetLength]);

  const completedMetres = useMemo(() => {
    if (selectedActivity === "all" || !progressPercentage) return null;
    return Math.round((progressPercentage / 100) * assetLength);
  }, [progressPercentage, assetLength, selectedActivity]);

  /* ----- Whether the test overlay applies (chainage assets only) ----- */
  const isLinear = selectedTypeMeta.measurement === "chainage";

  /* ----- Tests for the selected asset ----- */
  const assetTests = useMemo(() => {
    if (!selectedAsset) return [];
    return tests
      .map((t) => ({ ...t, _status: computeTestStatus(t) }))
      .filter((t) => {
        if (t.assetId && t.assetId === selectedAsset.id) return true;
        // Point tests captured without a linked asset still show if their
        // chainage falls inside this asset's bounds.
        if (!t.assetId) {
          const c = Number(t.chainageStart);
          return Number.isFinite(c) && c >= assetStart && c <= assetEnd;
        }
        return false;
      })
      .filter((t) => (testTypeFilter === "all" ? true : t.testType === testTypeFilter));
  }, [tests, selectedAsset, assetStart, assetEnd, testTypeFilter]);

  const markerTests = useMemo(() => {
    const point = [];
    const range = [];
    const asset = [];
    assetTests.forEach((t) => {
      const mode = TEST_TYPE_META[t.testType]?.locationMode;
      if (mode === "asset") { asset.push(t); return; }
      const cs = Number(t.chainageStart);
      const ce = Number(t.chainageEnd);
      if (mode === "range" && Number.isFinite(cs) && Number.isFinite(ce) && ce > cs) {
        range.push(t);
      } else if (Number.isFinite(cs)) {
        point.push(t);
      }
    });
    return { point, range, asset };
  }, [assetTests]);

  /* ----- Coverage analysis (where testing is short) ----- */
  const coverage = useMemo(() => {
    if (!selectedAsset) return null;
    const relevant = assetTests.filter((t) => t.testType === coverageType);
    const intervals = relevant
      .map((t) => ({
        start: clamp(Number(t.chainageStart), assetStart, assetEnd),
        end: clamp(Number(t.chainageEnd ?? t.chainageStart), assetStart, assetEnd),
      }))
      .filter((i) => Number.isFinite(i.start) && Number.isFinite(i.end) && i.end > i.start);

    const merged = mergeIntervals(intervals);
    const coveredLen = merged.reduce((sum, i) => sum + (i.end - i.start), 0);
    const pct = clamp((coveredLen / assetLength) * 100, 0, 100);

    const gaps = [];
    let cursor = assetStart;
    merged.forEach((i) => {
      if (i.start > cursor) gaps.push({ start: cursor, end: i.start });
      cursor = Math.max(cursor, i.end);
    });
    if (cursor < assetEnd) gaps.push({ start: cursor, end: assetEnd });

    const spacing = Number(maxSpacing) || 0;
    const shortGaps = gaps
      .map((g) => ({ ...g, length: g.end - g.start }))
      .filter((g) => spacing > 0 && g.length > spacing)
      .sort((a, b) => b.length - a.length);

    return { merged, gaps, shortGaps, pct, coveredLen, count: relevant.length };
  }, [assetTests, coverageType, selectedAsset, assetStart, assetEnd, assetLength, maxSpacing]);

  /* ----- Actions ----- */

  /* The KMZ defines the asset's geography, so a chainage asset's end value is
     read off the imported route rather than typed. Non-chainage assets keep
     their own units (floors, components, m³) and the file only supplies the
     geography. */
  const newTypeMeasurement = (ASSET_TYPES[assetType] || ASSET_TYPES.pipeline).measurement;
  const kmzRequired = newTypeMeasurement === "chainage" || assetType === "building";
  const derivedEndChainage =
    newTypeMeasurement === "chainage" && kmzGeometry?.ok
      ? Number(startChainage || 0) + Math.round(kmzGeometry.lengthM)
      : null;

  async function handleKmzPicked(file) {
    if (!file) return;
    setKmzBusy(true);
    setKmzError("");
    setKmzFile(file);
    setKmzGeometry(null);
    try {
      const parsed = await parseKmlFile(file);
      const derived = deriveAssetGeometry(parsed, assetType, { measurement: newTypeMeasurement });
      if (!derived.ok) {
        setKmzError(derived.error);
        return;
      }
      setKmzGeometry(derived);
    } catch (err) {
      setKmzError(err?.message || "Could not read that file.");
    } finally {
      setKmzBusy(false);
    }
  }

  function clearKmz() {
    setKmzFile(null);
    setKmzGeometry(null);
    setKmzError("");
    if (kmzInputRef.current) kmzInputRef.current.value = "";
  }

  async function createAsset() {
    if (!projectId) return;

    const start = Number(startChainage);

    if (!assetName.trim()) {
      alert("Please enter an asset name.");
      return;
    }
    if (kmzRequired && !kmzGeometry?.ok) {
      alert(
        assetType === "building"
          ? "Select a KMZ holding the four corners of the building — the footprint comes from the file."
          : "Select a KMZ for this asset — its length is measured from the imported route."
      );
      return;
    }

    const geoFields = kmzGeometry?.ok
      ? assetFieldsFromGeometry(kmzGeometry, {
          measurement: newTypeMeasurement,
          startValue: start,
          fileName: kmzFile?.name,
        })
      : {};

    // Only a chainage asset takes its extent from the file; the rest keep the
    // start/end the user entered in their own units.
    const extent =
      newTypeMeasurement === "chainage"
        ? {}
        : {
            startChainage: start,
            endChainage: Number(endChainage),
            totalLength: Number(endChainage) - start,
          };

    if (newTypeMeasurement !== "chainage" && Number(endChainage) <= start) {
      alert("End value must be greater than start value.");
      return;
    }

    await addDoc(collection(db, "projects", projectId, "progressAssets"), {
      projectId,
      assetType,
      assetName: assetName.trim(),
      ...extent,
      ...geoFields,
      // Default trench dimensions — measurement lines seed their width and
      // depth from these rather than the catalogue's guess.
      trenchWidthM: trenchWidthM === "" ? null : Number(trenchWidthM),
      trenchDepthM: trenchDepthM === "" ? null : Number(trenchDepthM),
      routeUpdatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    setAssetName("");
    setStartChainage(0);
    setEndChainage(3250);
    setTrenchWidthM("");
    setTrenchDepthM("");
    clearKmz();
  }

  function findChainage() {
    if (!selectedAsset) return;
    const ch = Number(chainageSearch);
    if (Number.isNaN(ch)) { alert("Enter a valid chainage number."); return; }
    const match = filteredSegments.find((seg) => {
      const s = Number(seg.chainageStart); const e = Number(seg.chainageEnd);
      return ch >= s && ch <= e;
    });
    if (match) {
      setActiveSegmentId(match.id);
    } else {
      alert(`No segment covers chainage ${ch} for the current activity filter.`);
    }
  }

  // Resolve the site-diary document linked to a segment (by diaryId, then diaryNo).
  function openDiaryForSegment(seg) {
    if (!seg) return;
    const found =
      diaries.find((d) => d.id === seg.diaryId) ||
      diaries.find((d) => seg.diaryNo && (d.diaryNo === seg.diaryNo || d.id === seg.diaryNo)) ||
      null;
    setOpenDiary(
      found || {
        _missing: true,
        diaryNo: seg.diaryNo || "—",
        date: seg.diaryDate || "",
        worksPerformed: seg.notes || "",
      }
    );
  }

  /* ----- Activity options for current asset type ----- */

  const newAssetTypeMeta = ASSET_TYPES[assetType] || ASSET_TYPES.pipeline;
  const activityOptions = useMemo(
    () => activityFilterOptions(selectedTypeMeta.activities || []),
    [selectedTypeMeta]
  );

  /* ----- Empty project guard ----- */

  if (!projectId) {
    return (
      <div style={styles.page}>
        <section style={styles.card}>
          <h1 style={styles.title}>Visual Progress Tracker</h1>
          <div style={styles.empty}>Select a project first to create and view visual progress.</div>
        </section>
      </div>
    );
  }

  /* ----- Render ----- */

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes pipe3dTooltipIn {
          from { opacity: 0; transform: translateX(-50%) translateY(6px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes vptPulse {
          0%, 100% { opacity: 0.35; }
          50%      { opacity: 0.9; }
        }
        @keyframes vptTooltipIn {
          from { opacity: 0; transform: translate(-50%, 4px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
      {/* Header with title + stat + presentation launcher */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Visual Progress Tracker</h1>
          <p style={styles.subtitle}>
            Interactive progress view linked to Daily Diaries — updated in real time.
          </p>
        </div>

        <div style={styles.headerRight}>
          <div style={styles.statCard}>
            <span style={styles.statLabel}>
              {selectedActivity === "all" ? "Layered Overview" : "Completed Progress"}
            </span>
            <strong style={styles.statValue}>
              {progressPercentage === null ? "—" : `${progressPercentage.toFixed(1)}%`}
            </strong>
            {completedMetres !== null && (
              <span style={styles.statSub}>
                {completedMetres.toLocaleString("en-ZA")} of {assetLength.toLocaleString("en-ZA")} m
              </span>
            )}
          </div>

          <button
            style={{
              ...styles.presentationButton,
              opacity: selectedAsset ? 1 : 0.5,
              pointerEvents: selectedAsset ? "auto" : "none",
            }}
            onClick={() => setPresentationOpen(true)}
            title="Open the satellite progress plan — the route in plan with each section coloured by status"
          >
            <span style={{ fontSize: 18 }}>🛰️</span> Satellite Plan
          </button>
        </div>
      </div>

      {/* Asset setup */}
      <section style={styles.card}>
        <h2 style={styles.cardTitle}>Asset Setup</h2>

        <div style={styles.setupGrid}>
          <label style={styles.label}>
            Asset Type
            <select
              style={styles.input}
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
            >
              {Object.entries(ASSET_TYPES).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.icon} {meta.label}
                </option>
              ))}
            </select>
            <span style={styles.helperText}>{newAssetTypeMeta.description}</span>
          </label>

          <label style={styles.label}>
            Asset Name
            <input
              style={styles.input}
              value={assetName}
              onChange={(e) => setAssetName(e.target.value)}
              placeholder={`e.g. ${newAssetTypeMeta.label} 01`}
            />
          </label>

          <label style={styles.label}>
            {newAssetTypeMeta.measurement === "chainage" ? "Start Chainage" :
             newAssetTypeMeta.measurement === "floors" ? "Start Floor" :
             newAssetTypeMeta.measurement === "volume" ? "Start (m³)" : "Start"}
            <input
              style={styles.input}
              type="number"
              value={startChainage}
              onChange={(e) => setStartChainage(e.target.value)}
            />
          </label>

          <label style={styles.label}>
            {newAssetTypeMeta.measurement === "chainage" ? "End Chainage" :
             newAssetTypeMeta.measurement === "floors" ? "End Floor" :
             newAssetTypeMeta.measurement === "volume" ? "End (m³)" : "End"}
            {newAssetTypeMeta.measurement === "chainage" ? (
              <>
                <div style={styles.derivedValue}>
                  {derivedEndChainage === null
                    ? "— from KMZ —"
                    : `CH ${derivedEndChainage.toLocaleString("en-ZA")}`}
                </div>
                <span style={styles.helperText}>
                  Measured from the imported route, not typed.
                </span>
              </>
            ) : (
              <input
                style={styles.input}
                type="number"
                value={endChainage}
                onChange={(e) => setEndChainage(e.target.value)}
              />
            )}
          </label>
        </div>

        {/* Trench dimensions — the default properties measurement lines use */}
        <div style={styles.geoGrid}>
          <label style={styles.label}>
            Trench Width (m)
            <input style={styles.input} type="number" step="any" placeholder="0.90"
              value={trenchWidthM} onChange={(e) => setTrenchWidthM(e.target.value)} />
            <span style={styles.helperText}>Seeds the width on every measurement line.</span>
          </label>
          <label style={styles.label}>
            Trench Depth (m)
            <input style={styles.input} type="number" step="any" placeholder="1.50"
              value={trenchDepthM} onChange={(e) => setTrenchDepthM(e.target.value)} />
            <span style={styles.helperText}>Used where an item has no thickness of its own.</span>
          </label>
        </div>

        {/* ── Geography: one KMZ, which is also what measures the asset ── */}
        <div style={styles.kmzBlock}>
          <input
            ref={kmzInputRef}
            type="file"
            accept=".kml,.kmz"
            style={{ display: "none" }}
            onChange={(e) => handleKmzPicked(e.target.files?.[0])}
          />

          <div style={styles.kmzHead}>
            <div>
              <strong style={styles.kmzTitle}>
                {assetType === "building" ? "Building footprint" : "Route"}
                {kmzRequired && <span style={styles.kmzRequired}>required</span>}
              </strong>
              <p style={styles.kmzHint}>
                {assetType === "building"
                  ? "Select a KMZ holding the four corners of the building. The plan area and perimeter are measured from it."
                  : newTypeMeasurement === "chainage"
                  ? "Select a KMZ of the route. The asset's length — and therefore its end chainage — is measured from it."
                  : "Optional. Select a KMZ to place this asset on the satellite plan."}
              </p>
            </div>
            <div style={styles.kmzActions}>
              <button
                style={styles.kmzButton}
                onClick={() => kmzInputRef.current?.click()}
                disabled={kmzBusy}
              >
                {kmzBusy ? "Reading…" : kmzFile ? "Change KMZ" : "📍 Select KMZ"}
              </button>
              {kmzFile && (
                <button style={styles.kmzClear} onClick={clearKmz} disabled={kmzBusy}>
                  Remove
                </button>
              )}
            </div>
          </div>

          {kmzError && <div style={styles.kmzError}>{kmzError}</div>}

          {kmzGeometry?.ok && (
            <div style={styles.kmzSummary}>
              <span style={styles.kmzFileChip}>📄 {kmzFile?.name}</span>
              {kmzGeometry.kind === "footprint" ? (
                <>
                  <KmzStat label="Corners" value={kmzGeometry.corners.length} />
                  <KmzStat label="Plan area" value={`${Math.round(kmzGeometry.areaM2).toLocaleString("en-ZA")} m²`} />
                  <KmzStat label="Perimeter" value={`${Math.round(kmzGeometry.perimeterM).toLocaleString("en-ZA")} m`} />
                </>
              ) : (
                <>
                  <KmzStat label="Points" value={kmzGeometry.coords.length.toLocaleString("en-ZA")} />
                  <KmzStat label="Length" value={`${Math.round(kmzGeometry.lengthM).toLocaleString("en-ZA")} m`} />
                  {derivedEndChainage !== null && (
                    <KmzStat
                      label="Chainage"
                      value={`CH ${Number(startChainage || 0).toLocaleString("en-ZA")} → CH ${derivedEndChainage.toLocaleString("en-ZA")}`}
                    />
                  )}
                </>
              )}
              {kmzGeometry.markers.length > 0 && (
                <KmzStat label="Markers" value={kmzGeometry.markers.length} />
              )}
              {kmzGeometry.warning && <span style={styles.kmzWarn}>⚠ {kmzGeometry.warning}</span>}
            </div>
          )}
        </div>

        <div style={styles.setupActions}>
          <span style={styles.helperText}>
            {assetType === "building"
              ? "Draw the four corners of the building with the Polygon tool in Google Earth, then Save Place As → KMZ."
              : "Draw the asset as a Path in Google Earth, then Save Place As → KMZ."}
          </span>
          <button
            style={{
              ...styles.primaryButton,
              opacity: kmzRequired && !kmzGeometry?.ok ? 0.5 : 1,
              cursor: kmzRequired && !kmzGeometry?.ok ? "not-allowed" : "pointer",
            }}
            onClick={createAsset}
            disabled={kmzRequired && !kmzGeometry?.ok}
          >
            + Add Asset
          </button>
        </div>
      </section>

      {/* Selector toolbar */}
      <section style={styles.card}>
        <div style={styles.toolbar}>
          <label style={styles.label}>
            Select Asset
            <select
              style={styles.input}
              value={selectedAssetId}
              onChange={(e) => {
                setSelectedAssetId(e.target.value);
                setActiveSegmentId(null);
                setSelectedActivity("all");
              }}
            >
              {assets.length === 0 && <option value="">— no assets yet —</option>}
              {assets.map((asset) => {
                const m = ASSET_TYPES[asset.assetType] || ASSET_TYPES.pipeline;
                return (
                  <option key={asset.id} value={asset.id}>
                    {m.icon} {asset.assetName}
                  </option>
                );
              })}
            </select>
          </label>

          <label style={styles.label}>
            Activity Layer
            <select
              style={styles.input}
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
            >
              <option value="all">All Activities</option>
              {activityOptions.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </label>

          <label style={styles.label}>
            Go to Position
            <div style={styles.searchRow}>
              <input
                style={styles.input}
                type="number"
                placeholder="e.g. 1250"
                value={chainageSearch}
                onChange={(e) => setChainageSearch(e.target.value)}
              />
              <button style={styles.secondaryButton} onClick={findChainage}>Find</button>
            </div>
          </label>
        </div>

        {selectedAsset ? (
          <>
            {/* Polished asset summary card */}
            <div style={{ ...styles.assetSummary, borderTop: `3px solid ${selectedTypeMeta.accent}` }}>
              <div style={styles.summaryLeft}>
                <span style={styles.summaryIcon}>{selectedTypeMeta.icon}</span>
                <div>
                  <strong style={styles.summaryName}>{selectedAsset.assetName}</strong>
                  <span style={styles.summaryType}>{selectedTypeMeta.label}</span>
                </div>
              </div>
              <div style={styles.summaryRight}>
                <span>{formatChainage(assetStart)} → {formatChainage(assetEnd)}</span>
                <span style={styles.summaryLength}>
                  {assetLength.toLocaleString("en-ZA")} m total
                </span>
                {selectedAsset.footprintCoords?.length > 0 ? (
                  <span style={styles.routeBadgeOk}>
                    🏗️ Footprint · {selectedAsset.footprintCorners || selectedAsset.footprintCoords.length} corners
                    {selectedAsset.footprintAreaM2
                      ? ` · ${Math.round(selectedAsset.footprintAreaM2).toLocaleString("en-ZA")} m²`
                      : ""}
                  </span>
                ) : selectedAsset.routeCoords?.length > 0 ? (
                  <span style={styles.routeBadgeOk}>
                    🗺️ Route loaded · {selectedAsset.routeCoords.length} points
                    {selectedAsset.routeLengthMetres
                      ? ` · ${selectedAsset.routeLengthMetres.toLocaleString("en-ZA")} m`
                      : ""}
                  </span>
                ) : (
                  <span style={styles.routeBadgeMissing}>
                    🗺️ No KMZ imported
                  </span>
                )}
                <button
                  style={styles.routeImportButton}
                  onClick={() => setKmzImporterOpen(true)}
                >
                  {selectedAsset.routeCoords?.length > 0 ? "Replace KMZ" : "Select KMZ"}
                </button>
              </div>
            </div>

            {/* ── Quality-test overlay controls (linear assets only) ── */}
            {isLinear && (
              <div style={styles.testControls}>
                <label style={styles.testToggle}>
                  <input
                    type="checkbox"
                    checked={showTests}
                    onChange={(e) => setShowTests(e.target.checked)}
                  />
                  Show test markers
                </label>
                <select
                  style={styles.miniSelect}
                  value={testTypeFilter}
                  onChange={(e) => setTestTypeFilter(e.target.value)}
                  disabled={!showTests}
                >
                  <option value="all">All test types</option>
                  {TEST_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                <span style={styles.testLegend}>
                  <span><i style={{ background: RESULT_COLORS.pass.fill }} /> Pass</span>
                  <span><i style={{ background: RESULT_COLORS.fail.fill }} /> Fail</span>
                  <span><i style={{ background: RESULT_COLORS.pending.fill }} /> Pending</span>
                </span>
              </div>
            )}

            {/* The trench cross-section */}
            <div style={styles.pipeWrapper}>
              <TrenchSection
                assetSegments={assetSegments}
                filteredSegments={filteredSegments}
                selectedActivity={selectedActivity}
                setSelectedActivity={setSelectedActivity}
                assetStart={assetStart}
                assetEnd={assetEnd}
                assetLength={assetLength}
                tests={isLinear && showTests ? assetTests : []}
                hoveredSegmentId={hoveredSegmentId}
                setHoveredSegmentId={setHoveredSegmentId}
                activeSegmentId={activeSegmentId}
                onSegmentClick={(seg) => setActiveSegmentId(seg.id)}
                onDiaryClick={openDiaryForSegment}
                onTestClick={(t) => setOpenTest(t)}
                onMeasureClick={(seg) => setMeasureSegment(seg)}
                measurementCountBySegment={measurementCountBySegment}
              />
            </div>

            {/* Legend */}
            <div style={styles.legend}>
              {[
                ["completed", "Completed"],
                ["in_progress", "In Progress"],
                ["delayed", "Delayed"],
                ["rework", "Rework"],
                ["not_started", "Not Started"],
              ].map(([key, label]) => (
                <span key={key} style={styles.legendChip}>
                  <span style={{ ...styles.legendDot, background: STATUS_COLOURS[key] }} />
                  {label}
                </span>
              ))}
              {selectedActivity === "all" && (
                <span style={styles.legendNote}>Select one activity layer to calculate % progress.</span>
              )}
            </div>
          </>
        ) : (
          <div style={styles.empty}>
            No asset created yet. Add one above to start tracking progress.
          </div>
        )}
      </section>

      {/* ── Productivity analytics ── */}
      {selectedAsset && (
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Productivity</h2>
          <ProductivityView
            asset={selectedAsset}
            segments={assetSegments}
            diariesById={diariesById}
            measurements={assetMeasurements}
            programmeActivities={[]}
          />
        </section>
      )}

      {/* ── Test coverage (where testing is short) ── */}
      {selectedAsset && isLinear && (
        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Test Coverage</h2>

          <div style={styles.coverageControls}>
            <label style={styles.label}>
              Coverage for
              <select style={styles.input} value={coverageType} onChange={(e) => setCoverageType(e.target.value)}>
                {TEST_TYPES.filter((t) => t.locationMode === "range").map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
                {TEST_TYPES.filter((t) => t.locationMode === "point").map((t) => (
                  <option key={t.key} value={t.key}>{t.label} (point)</option>
                ))}
              </select>
            </label>

            <label style={styles.label}>
              Max test spacing (m)
              <input
                style={styles.input}
                type="number"
                value={maxSpacing}
                onChange={(e) => setMaxSpacing(e.target.value)}
              />
            </label>

            {coverage && (
              <div style={styles.coverageStat}>
                <span style={styles.coverageStatLabel}>{coverageType} coverage</span>
                <strong style={styles.coverageStatValue}>{coverage.pct.toFixed(1)}%</strong>
                <span style={styles.coverageStatSub}>{coverage.count} test{coverage.count === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>

          {coverage && (
            <>
              <div style={styles.coverageBar}>
                {coverage.merged.map((iv, i) => {
                  const left = ((iv.start - assetStart) / assetLength) * 100;
                  const width = ((iv.end - iv.start) / assetLength) * 100;
                  return (
                    <div
                      key={`cov-${i}`}
                      title={`Tested ${formatChainage(iv.start)} – ${formatChainage(iv.end)}`}
                      style={{ ...styles.coverageFill, left: `${left}%`, width: `${Math.max(width, 0.4)}%` }}
                    />
                  );
                })}
                {coverage.shortGaps.map((g, i) => {
                  const left = ((g.start - assetStart) / assetLength) * 100;
                  const width = ((g.end - g.start) / assetLength) * 100;
                  return (
                    <div
                      key={`gap-${i}`}
                      title={`Untested gap ${formatChainage(g.start)} – ${formatChainage(g.end)} (${Math.round(g.length)} m)`}
                      style={{ ...styles.coverageGap, left: `${left}%`, width: `${Math.max(width, 0.4)}%` }}
                    />
                  );
                })}
              </div>

              {coverage.shortGaps.length === 0 ? (
                <p style={styles.coverageOk}>
                  No untested stretches longer than {maxSpacing} m for {coverageType}.
                </p>
              ) : (
                <div style={styles.gapList}>
                  <div style={styles.gapListTitle}>
                    {coverage.shortGaps.length} stretch{coverage.shortGaps.length === 1 ? "" : "es"} exceed {maxSpacing} m without a {coverageType} test:
                  </div>
                  {coverage.shortGaps.slice(0, 8).map((g, i) => (
                    <div key={i} style={styles.gapRow}>
                      <span style={styles.gapBadge}>{Math.round(g.length)} m</span>
                      <span>{formatChainage(g.start)} → {formatChainage(g.end)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Satellite progress plan (replaces the old 2.5D / 3D presentation) */}
      {presentationOpen && selectedAsset && (
        <SatelliteProgressPlan
          asset={selectedAsset}
          assetTypeMeta={selectedTypeMeta}
          segments={segments.filter((s) => s.assetId === selectedAsset.id)}
          onClose={() => setPresentationOpen(false)}
        />
      )}

      {/* Daily measurements for one recorded section */}
      {measureSegment && (
        <MeasurementSheet
          projectId={projectId}
          asset={selectedAsset}
          segment={measureSegment}
          onClose={() => setMeasureSegment(null)}
        />
      )}

      {/* KMZ / KML route importer */}
      {kmzImporterOpen && selectedAsset && (
        <KmzRouteImporter
          projectId={projectId}
          asset={selectedAsset}
          onDone={() => { /* live snapshot will refresh the asset doc */ }}
          onClose={() => setKmzImporterOpen(false)}
        />
      )}

      {/* Test detail popup */}
      {openTest && <TestDetailPopup test={openTest} onClose={() => setOpenTest(null)} />}

      {/* Site diary popup (opened from a segment's diary reference) */}
      {openDiary && <DiaryPopup diary={openDiary} onClose={() => setOpenDiary(null)} />}
    </div>
  );
}

function KmzStat({ label, value }) {
  return (
    <span style={styles.kmzStat}>
      <span style={styles.kmzStatLabel}>{label}</span>
      <strong style={styles.kmzStatValue}>{value}</strong>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* TrenchSection — typical pipeline trench cross-section.               */
/* Horizontal axis is chainage; vertical bands are the trench layers    */
/* from surface (top) to invert (bottom). The pipe sits in the pipe-     */
/* laying zone; concrete encasement is drawn as a grey dashed overlay    */
/* over the pipe; quality tests show as chevrons on the top surface.     */
/* ------------------------------------------------------------------ */

export function TrenchSection({
  assetSegments,
  filteredSegments,
  selectedActivity,
  setSelectedActivity,
  assetStart,
  assetEnd,
  assetLength,
  tests,
  hoveredSegmentId,
  setHoveredSegmentId,
  activeSegmentId,
  onSegmentClick,
  onDiaryClick,
  onTestClick,
  onMeasureClick,
  measurementCountBySegment = {},
}) {
  // ── Existing services encountered along the route ──
  // Each service type gets its own horizontal lane above the trench so the
  // bars stack one above the other (never on top of one another).
  const serviceSegments = assetSegments.filter((s) => isServiceActivity(s.activity));
  const serviceRowKeys = [
    ...SERVICE_TYPES.map((s) => s.key).filter((key) =>
      serviceSegments.some((s) => (s.serviceType || "") === key)
    ),
    ...(serviceSegments.some((s) => !SERVICE_TYPE_BY_KEY[s.serviceType || ""]) ? [UNKNOWN_SERVICE.key] : []),
  ];
  const SERVICE_ROW_H = 20;
  const servicesLaneH = serviceRowKeys.length ? serviceRowKeys.length * SERVICE_ROW_H + 12 : 0;
  const serviceRowIndex = Object.fromEntries(serviceRowKeys.map((key, i) => [key, i]));

  // ── Traffic management (TMP) ──
  // A chainage range like an adjoining service, but it is accommodation of
  // traffic rather than something in the ground, so it owns the top lane.
  const tmpSegments = assetSegments.filter((s) => isTmpActivity(s.activity));
  const TMP_ROW_H = 18;
  const tmpLaneH = tmpSegments.length ? TMP_ROW_H + 8 : 0;

  // ── Surface works (site clearance / tar cutting / tar removing) ──
  // Not trench layers — they get a row each above the road surface strip.
  // Site clearance splits further into its two measured sub-sections, so a
  // clearance row is keyed "site_clearance:<subType>" and each sub-section
  // draws on its own line.
  const surfaceSegments = assetSegments.filter((s) => isSurfaceActivity(s.activity));
  const surfaceRowKeyFor = (seg) =>
    seg.activity === "site_clearance"
      ? `site_clearance:${seg.clearanceSubType || ""}`
      : seg.activity;

  const surfaceRowKeys = [];
  SURFACE_ACTIVITY_KEYS.forEach((key) => {
    if (key !== "site_clearance") {
      if (surfaceSegments.some((s) => s.activity === key)) surfaceRowKeys.push(key);
      return;
    }
    // Ordered sub-sections first, then any clearance recorded without one.
    CLEARANCE_SUBTYPES.forEach((sub) => {
      if (surfaceSegments.some((s) => s.activity === "site_clearance" && s.clearanceSubType === sub.key)) {
        surfaceRowKeys.push(`site_clearance:${sub.key}`);
      }
    });
    if (surfaceSegments.some((s) => s.activity === "site_clearance" && !CLEARANCE_SUBTYPE_BY_KEY[s.clearanceSubType || ""])) {
      surfaceRowKeys.push("site_clearance:");
    }
  });

  const SURFACE_ROW_H = 15;
  const surfaceLaneH = surfaceRowKeys.length ? surfaceRowKeys.length * SURFACE_ROW_H + 8 : 0;
  const surfaceRowIndex = Object.fromEntries(surfaceRowKeys.map((key, i) => [key, i]));

  // Row label / accent for a surface row key.
  const surfaceRowMeta = (key) => {
    if (!key.startsWith("site_clearance")) {
      return { label: ACTIVITY_LABELS[key] || key, colour: "#475569", activity: key, sub: "" };
    }
    const sub = key.slice("site_clearance:".length);
    const meta = clearanceSubTypeMeta(sub);
    return {
      label: sub ? `Clearance ${meta.prefix} ${meta.label}` : "Site Clearance",
      colour: sub ? meta.colour : "#475569",
      activity: "site_clearance",
      sub,
    };
  };

  // ── Structures on the pipe (concrete encasement / culverts) ──
  // Not trench layers — drawn over the pipe they buried the pipe-laying band,
  // so each gets its own row in a lane under the trench.
  const structureSegments = assetSegments.filter((s) => isStructureActivity(s.activity));
  const structureRows = STRUCTURE_ACTIVITIES.filter((row) =>
    structureSegments.some((s) => s.activity === row.key)
  );
  const STRUCTURE_ROW_H = 20;
  const structuresLaneH = structureRows.length ? structureRows.length * STRUCTURE_ROW_H + 10 : 0;
  const structureRowIndex = Object.fromEntries(structureRows.map((row, i) => [row.key, i]));

  // ── SVG canvas geometry (scales responsively, uniform aspect) ──
  const W = 1100;
  const H = 392 + tmpLaneH + servicesLaneH + surfaceLaneH + structuresLaneH;
  const LABEL_W = 150;            // left label column
  const DX0 = LABEL_W + 14;       // drawing area left
  const DX1 = W - 26;             // drawing area right
  const DW = DX1 - DX0;
  const CHEVRON_Y = 16;           // chevron tip lane (top)
  const TMP_LANE_TOP = 30;        // top of the traffic-management lane
  const SURFACE_LANE_TOP = 38 + tmpLaneH;          // top of the surface-works lane
  const SURFACE_Y = 46 + tmpLaneH + surfaceLaneH;  // road surface line
  const SERVICES_TOP = SURFACE_Y + 6;              // top of the services lane
  const BAND_TOP = SURFACE_Y + 8 + servicesLaneH;  // top of soil bands
  const AXIS_Y = H - 30;          // chainage axis baseline
  // The trench keeps exactly its former height — the structures lane occupies
  // the extra canvas added for it, between the trench invert and the axis.
  const bandsBottom = AXIS_Y - 18 - structuresLaneH;
  const STRUCTURES_TOP = bandsBottom + 10;   // top of the structures lane
  const BATTER = 0;               // rectangular trench (no wall batter / corner wedges)

  // Centre-line y of a given service lane row.
  const serviceRowY = (key) =>
    SERVICES_TOP + 6 + (serviceRowIndex[key] ?? 0) * SERVICE_ROW_H + SERVICE_ROW_H / 2;

  // Centre-line y of the traffic-management lane row.
  const tmpRowY = TMP_LANE_TOP + 4 + TMP_ROW_H / 2;

  // Centre-line y of a given surface-works row.
  const surfaceRowY = (key) =>
    SURFACE_LANE_TOP + 4 + (surfaceRowIndex[key] ?? 0) * SURFACE_ROW_H + SURFACE_ROW_H / 2;

  // Centre-line y of a given structures row.
  const structureRowY = (key) =>
    STRUCTURES_TOP + 5 + (structureRowIndex[key] ?? 0) * STRUCTURE_ROW_H + STRUCTURE_ROW_H / 2;

  // Weighted band heights so the pipe zone reads larger.
  const W_BY_KEY = {
    reinstatement: 1.15, backfill: 1.15, bedding: 0.8, pipe_laying: 1.35,
    welding: 0.7, testing: 0.7, crusher: 1.0, dump_rock: 1.0, excavation: 1.05,
  };
  const totalWeight = TRENCH_LAYERS.reduce((s, l) => s + (W_BY_KEY[l.key] || 1), 0);
  const bandsHeight = bandsBottom - BAND_TOP;
  let acc = BAND_TOP;
  const bands = TRENCH_LAYERS.map((l) => {
    const h = (bandsHeight * (W_BY_KEY[l.key] || 1)) / totalWeight;
    const band = { ...l, y: acc, h };
    acc += h;
    return band;
  });
  const bandByKey = Object.fromEntries(bands.map((b) => [b.key, b]));
  const pipeBand = bandByKey.pipe_laying;
  const pipeCY = pipeBand.y + pipeBand.h * 0.55;
  const pipeRY = Math.min(pipeBand.h * 0.45, 17);

  const xFor = (ch) =>
    DX0 + ((clamp(Number(ch), assetStart, assetEnd) - assetStart) / assetLength) * DW;

  // Trapezoid (battered trench) clip.
  const trap = `${DX0},${BAND_TOP} ${DX1},${BAND_TOP} ${DX1 - BATTER},${bandsBottom} ${DX0 + BATTER},${bandsBottom}`;

  // Chainage ruler ticks (100 m minor / 500 m major).
  const ticks = [];
  {
    const minor = 100, major = 500;
    const first = Math.ceil(assetStart / minor) * minor;
    for (let v = first; v < assetEnd; v += minor) {
      ticks.push({ v, x: xFor(v), isMajor: v % major === 0 });
    }
  }

  // Segments to draw on the bands: every layer in "all" mode, otherwise the filtered set.
  const drawSegments = selectedActivity === "all" ? assetSegments : filteredSegments;
  // Feature the actively-selected segment; otherwise prefer an in-progress one.
  const featured =
    (activeSegmentId && assetSegments.find((s) => s.id === activeSegmentId)) ||
    filteredSegments.find((s) => s.status === "in_progress") ||
    filteredSegments[0] ||
    null;

  const activityLabel = (a) => ACTIVITY_LABELS[a] || a;

  return (
    <div style={ts.card}>
      {/* Header */}
      <div style={ts.head}>
        <div>
          <h3 style={ts.title}>Typical pipeline trench section</h3>
          <p style={ts.sub}>
            Horizontal axis is chainage; vertical bands represent the trench layers from surface to invert.
          </p>
        </div>
        {selectedActivity === "all" ? (
          <span style={ts.pillInfo}>🪨 All trench layers</span>
        ) : (
          <button style={ts.pillActive} onClick={() => setSelectedActivity("all")}>
            {activityFilterLabel(selectedActivity)} <span style={ts.pillX}>✕</span>
          </button>
        )}
      </div>

      {/* Featured / highlighted segment card — updates when a segment is selected */}
      {featured && (
        <div
          style={{
            ...ts.segCard,
            borderLeft: `5px solid ${STATUS_COLOURS[featured.status] || STATUS_COLOURS.not_started}`,
          }}
        >
          <div style={ts.segCardTop}>
            <span style={ts.segCardName}>
              <span
                style={{
                  ...ts.segDot,
                  background: STATUS_COLOURS[featured.status] || STATUS_COLOURS.not_started,
                }}
              />
              {activityLabel(featured.activity)}
            </span>
            <span style={ts.segCardStatus}>{(featured.status || "not started").replace(/_/g, " ")}</span>
          </div>
          <div style={ts.segCardRow}>
            {featured.activity === "services_crossing" ? (
              <span><b style={ts.segCardLbl}>Stake</b> {formatChainage(featured.chainageStart)}</span>
            ) : (
              <>
                <span><b style={ts.segCardLbl}>Range</b> {formatChainage(featured.chainageStart)} → {formatChainage(featured.chainageEnd)}</span>
                <span><b style={ts.segCardLbl}>Length</b> {Math.max(Number(featured.chainageEnd) - Number(featured.chainageStart), 0).toLocaleString("en-ZA")} m</span>
              </>
            )}
            {isServiceActivity(featured.activity) && (
              <span>
                <b style={ts.segCardLbl}>Service</b> {serviceMeta(featured.serviceType || "").label}
                {Number(featured.serviceDiameterMm) > 0 ? ` · Ø ${featured.serviceDiameterMm} mm` : ""}
              </span>
            )}
            {featured.activity === "excavation" && (
              <span>
                <b style={ts.segCardLbl}>Material</b>{" "}
                <span style={{ color: excavationConditionMeta(featured.excavationCondition).colour, fontWeight: 800 }}>
                  {excavationConditionMeta(featured.excavationCondition).icon}{" "}
                  {excavationConditionMeta(featured.excavationCondition).label}
                </span>
              </span>
            )}
            {featured.activity === "site_clearance" && (
              <span>
                <b style={ts.segCardLbl}>Sub-section</b>{" "}
                <span style={{ color: clearanceSubTypeMeta(featured.clearanceSubType).colour, fontWeight: 800 }}>
                  {clearanceSubTypeMeta(featured.clearanceSubType).prefix}{" "}
                  {clearanceSubTypeMeta(featured.clearanceSubType).label}
                </span>
              </span>
            )}
            {featured.diaryNo && (
              <span>
                <b style={ts.segCardLbl}>Diary</b>{" "}
                <button
                  type="button"
                  style={ts.diaryLink}
                  title="Open the linked site diary"
                  onClick={() => onDiaryClick(featured)}
                >
                  #{featured.diaryNo} ↗
                </button>
              </span>
            )}
          </div>
          {/* Measurement sheet for this section — the quantities measured
              against it are what roll up into the period summary. */}
          {onMeasureClick && (
            <div style={ts.segCardActions}>
              <button
                type="button"
                style={ts.measureBtn}
                onClick={() => onMeasureClick(featured)}
                title="Open the daily measurements table for this section"
              >
                📏 Daily Measurements
                {measurementCountBySegment[featured.id] > 0 && (
                  <span style={ts.measureBadge}>{measurementCountBySegment[featured.id]}</span>
                )}
              </button>
              <span style={ts.measureHint}>
                {measurementCountBySegment[featured.id] > 0
                  ? `${measurementCountBySegment[featured.id]} line${measurementCountBySegment[featured.id] === 1 ? "" : "s"} measured against this section.`
                  : "Nothing measured against this section yet."}
              </span>
            </div>
          )}

          <div style={ts.segCardHint}>
            {featured.diaryNo
              ? "Click a segment in the trench to load it here; click the diary reference to open the site diary."
              : "Click a segment in the trench to load it here."}
          </div>
        </div>
      )}

      {/* The drawing — wrapped so the floating tooltip can position over it */}
      <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={ts.svg}>
        <defs>
          <clipPath id="trenchClip">
            <polygon points={trap} />
          </clipPath>
          <linearGradient id="trenchPipe" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e2e8f0" />
            <stop offset="35%" stopColor="#cbd5e1" />
            <stop offset="60%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#64748b" />
          </linearGradient>
          <pattern id="trenchDots" width="12" height="12" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="3" r="1.5" fill="rgba(71,85,105,0.28)" />
            <circle cx="9" cy="9" r="1.5" fill="rgba(71,85,105,0.28)" />
          </pattern>
          <pattern id="trenchSoil" width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="9" stroke="rgba(176,138,82,0.25)" strokeWidth="1.4" />
          </pattern>
          <pattern id="vptCulvertHatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="7" stroke="rgba(15,118,110,0.55)" strokeWidth="1.6" />
          </pattern>

          {/* Wet excavation — a water table wash over the excavation band */}
          <pattern id="vptWetHatch" width="10" height="6" patternUnits="userSpaceOnUse">
            <path d="M 0 3 Q 2.5 0 5 3 T 10 3" fill="none" stroke="rgba(3,105,161,0.75)" strokeWidth="1.3" />
          </pattern>
          {/* Dry excavation — a dry, dusty stipple */}
          <pattern id="vptDryHatch" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.9" fill="rgba(180,83,9,0.55)" />
            <circle cx="6" cy="6" r="0.9" fill="rgba(180,83,9,0.55)" />
          </pattern>

          {/* ── Visual enhancement defs ── */}

          {/* Moving light band that sweeps along completed segments */}
          <linearGradient id="vptShimmer" gradientUnits="userSpaceOnUse"
            x1={DX0 - 200} y1="0" x2={DX0 - 60} y2="0">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity="0" />
            <stop offset="50%"  stopColor="#ffffff" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            <animate attributeName="x1" values={`${DX0 - 200};${DX1 + 60}`} dur="4.5s" repeatCount="indefinite" />
            <animate attributeName="x2" values={`${DX0 - 60};${DX1 + 200}`} dur="4.5s" repeatCount="indefinite" />
          </linearGradient>

          {/* Per-band vertical depth shading (top-light → bottom-shadow) */}
          <linearGradient id="vptBandDepth" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.22" />
            <stop offset="30%"  stopColor="#ffffff" stopOpacity="0" />
            <stop offset="82%"  stopColor="#0f172a" stopOpacity="0" />
            <stop offset="100%" stopColor="#0f172a" stopOpacity="0.10" />
          </linearGradient>

          {/* Soft blur for the pipe shadow */}
          <filter id="vptSoftBlur" x="-30%" y="-200%" width="160%" height="500%">
            <feGaussianBlur stdDeviation="4" />
          </filter>

          {/* Glow used by the pulsing in-progress halo */}
          <filter id="vptGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="3.2" />
          </filter>

          {/* One-shot reveal: segments draw in behind the scan line on load */}
          <clipPath id="vptRevealClip">
            <rect x={DX0} y={BAND_TOP - 10} height={bandsBottom - BAND_TOP + 20} width="0">
              <animate attributeName="width" from="0" to={DW} dur="1.4s" fill="freeze"
                calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" values={`0;${DW}`} />
            </rect>
          </clipPath>
        </defs>

        {/* Soil wedges outside the battered trench */}
        <polygon
          points={`${DX0},${BAND_TOP} ${DX0},${bandsBottom} ${DX0 + BATTER},${bandsBottom}`}
          fill="#efe7d6"
        />
        <polygon
          points={`${DX0},${BAND_TOP} ${DX0},${bandsBottom} ${DX0 + BATTER},${bandsBottom}`}
          fill="url(#trenchSoil)"
        />
        <polygon
          points={`${DX1},${BAND_TOP} ${DX1},${bandsBottom} ${DX1 - BATTER},${bandsBottom}`}
          fill="#efe7d6"
        />
        <polygon
          points={`${DX1},${BAND_TOP} ${DX1},${bandsBottom} ${DX1 - BATTER},${bandsBottom}`}
          fill="url(#trenchSoil)"
        />

        {/* ── Traffic management (TMP) lane ──
            Recorded CH start → CH finish exactly like an adjoining service,
            but drawn at the very top because it is accommodation of traffic
            around the works, not something buried in the trench. */}
        {tmpSegments.length > 0 && (
          <g>
            <rect
              x={DX0}
              y={TMP_LANE_TOP}
              width={DW}
              height={tmpLaneH}
              fill={TMP_COLOURS.soft}
              stroke="#fed7aa"
              strokeWidth="1"
              rx="3"
            />
            <line
              x1={DX0} y1={tmpRowY} x2={DX1} y2={tmpRowY}
              stroke={TMP_COLOURS.stroke} strokeWidth="1" strokeDasharray="2 4" opacity="0.35"
            />
            <text
              x={LABEL_W}
              y={tmpRowY + 4}
              textAnchor="end"
              fontSize="11"
              fontWeight="800"
              fill={selectedActivity === TMP_ACTIVITY ? "#b45309" : TMP_COLOURS.stroke}
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {ACTIVITY_LABELS[TMP_ACTIVITY]}
            </text>

            {tmpSegments.map((seg) => {
              const x = xFor(seg.chainageStart);
              const w = Math.max(xFor(seg.chainageEnd) - x, 3);
              const hovered = hoveredSegmentId === seg.id;
              const active = activeSegmentId === seg.id;
              const BAR_H = 11;
              const y = tmpRowY - BAR_H / 2;
              const statusColour = STATUS_COLOURS[seg.status] || STATUS_COLOURS.not_started;
              return (
                <g
                  key={seg.id}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoveredSegmentId(seg.id)}
                  onMouseLeave={() => setHoveredSegmentId(null)}
                  onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                >
                  <title>{`Traffic Management (TMP) · ${(seg.status || "").replace(/_/g, " ")}\n${formatChainage(seg.chainageStart)} → ${formatChainage(seg.chainageEnd)}`}</title>
                  <rect
                    x={x} y={y} width={w} height={BAR_H} rx={3}
                    fill={TMP_COLOURS.bar}
                    opacity={hovered || active ? 1 : 0.86}
                    stroke={active || hovered ? "#0f172a" : TMP_COLOURS.stroke}
                    strokeWidth={active ? 2 : hovered ? 1.5 : 1}
                  />
                  {/* Status strip so a TMP that is still pending reads as such */}
                  <rect x={x} y={y} width={w} height={3} fill={statusColour} pointerEvents="none" />
                  {/* Start / finish ticks — a TMP is set up and taken down at a
                      definite chainage, so both ends are marked. */}
                  <line x1={x} y1={y - 3} x2={x} y2={y + BAR_H + 3} stroke={TMP_COLOURS.stroke} strokeWidth="1.6" pointerEvents="none" />
                  <line x1={x + w} y1={y - 3} x2={x + w} y2={y + BAR_H + 3} stroke={TMP_COLOURS.stroke} strokeWidth="1.6" pointerEvents="none" />
                </g>
              );
            })}
          </g>
        )}

        {/* ── Surface works lane ──
            Site clearance, tar cutting and tar removing happen on the road
            surface, not in the trench, so they sit above the surface strip —
            one row each, coloured by status like every other segment. */}
        {surfaceRowKeys.length > 0 && (
          <g>
            <rect
              x={DX0}
              y={SURFACE_LANE_TOP}
              width={DW}
              height={surfaceLaneH}
              fill="#f1f5f9"
              stroke="#e2e8f0"
              strokeWidth="1"
              rx="3"
            />

            {surfaceRowKeys.map((key) => {
              const cy = surfaceRowY(key);
              const meta = surfaceRowMeta(key);
              const selected =
                selectedActivity === key ||
                selectedActivity === meta.activity ||
                (meta.sub && selectedActivity === `${meta.activity}@${meta.sub}`);
              return (
                <g key={`surf-row-${key}`}>
                  <line
                    x1={DX0} y1={cy} x2={DX1} y2={cy}
                    stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2 4" opacity="0.7"
                  />
                  <text
                    x={LABEL_W}
                    y={cy + 4}
                    textAnchor="end"
                    fontSize="11"
                    fontWeight="700"
                    fill={selected ? "#b45309" : meta.colour}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                  >
                    {meta.label}
                  </text>
                </g>
              );
            })}

            {surfaceSegments.map((seg) => {
              const x = xFor(seg.chainageStart);
              const w = Math.max(xFor(seg.chainageEnd) - x, 2);
              const rowKey = surfaceRowKeyFor(seg);
              const cy = surfaceRowY(rowKey);
              const colour = STATUS_COLOURS[seg.status] || STATUS_COLOURS.not_started;
              const hovered = hoveredSegmentId === seg.id;
              const active = activeSegmentId === seg.id;
              const subMeta = seg.activity === "site_clearance"
                ? clearanceSubTypeMeta(seg.clearanceSubType || "")
                : null;
              const BAR_H = 9;
              return (
                <g
                  key={seg.id}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoveredSegmentId(seg.id)}
                  onMouseLeave={() => setHoveredSegmentId(null)}
                  onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                >
                  <title>{`${activityLabel(seg.activity)}${subMeta && subMeta.key ? ` — ${subMeta.label}` : ""} · ${(seg.status || "").replace(/_/g, " ")}\n${formatChainage(seg.chainageStart)} → ${formatChainage(seg.chainageEnd)}`}</title>
                  <rect
                    x={x}
                    y={cy - BAR_H / 2}
                    width={w}
                    height={BAR_H}
                    rx={2}
                    fill={colour}
                    opacity={hovered || active ? 0.98 : 0.82}
                    stroke={active || hovered ? "#0f172a" : "rgba(15,23,42,0.35)"}
                    strokeWidth={active ? 2 : hovered ? 1.5 : 0.8}
                  />
                  {/* Sub-section tag on the crown so top soil and premix stay
                      distinguishable even when both rows are in view. */}
                  {subMeta && subMeta.key && (
                    <rect
                      x={x}
                      y={cy - BAR_H / 2}
                      width={w}
                      height={2.6}
                      fill={subMeta.colour}
                      pointerEvents="none"
                    />
                  )}
                </g>
              );
            })}
          </g>
        )}

        {/* Road surface strip */}
        <rect x={DX0} y={SURFACE_Y - 8} width={DW} height={8} fill="#6b7280" rx="1" />


        {/* Bands (clipped to the trench trapezoid) */}
        <g clipPath="url(#trenchClip)">
          {bands.map((b) => (
            <g key={b.key}>
              <rect x={DX0} y={b.y} width={DW} height={b.h + 0.5} fill={b.fill} />
              {b.dotted && <rect x={DX0} y={b.y} width={DW} height={b.h + 0.5} fill="url(#trenchDots)" />}
              <rect x={DX0} y={b.y} width={DW} height={b.h + 0.5} fill="url(#vptBandDepth)" pointerEvents="none" />
              {b.capStrip && <rect x={DX0} y={b.y} width={DW} height={4} fill={b.capStrip} opacity="0.65" />}
            </g>
          ))}

          {/* Soft shadow grounding the pipe in the trench */}
          <ellipse
            cx={DX0 + DW / 2}
            cy={pipeCY + pipeRY + 7}
            rx={DW / 2 - 24}
            ry="5.5"
            fill="rgba(15,23,42,0.28)"
            filter="url(#vptSoftBlur)"
            pointerEvents="none"
          />

          {/* The pipe */}
          <rect
            x={DX0 + 6}
            y={pipeCY - pipeRY}
            width={DW - 12}
            height={pipeRY * 2}
            rx={pipeRY}
            fill="url(#trenchPipe)"
            stroke="#0f172a"
            strokeWidth="1.4"
          />
          <rect
            x={DX0 + 10}
            y={pipeCY - pipeRY + 3}
            width={DW - 20}
            height={4}
            rx="2"
            fill="#ffffff"
            opacity="0.45"
          />
          <ellipse cx={DX0 + 6} cy={pipeCY} rx="6" ry={pipeRY} fill="#475569" stroke="#0f172a" strokeWidth="1.2" />
          <ellipse cx={DX1 - 6} cy={pipeCY} rx="6" ry={pipeRY} fill="#475569" stroke="#0f172a" strokeWidth="1.2" />

          {/* Progress segments per layer band — wrapped in the one-shot
              reveal clip so they draw in behind the scan line on load. */}
          <g clipPath="url(#vptRevealClip)">
          {drawSegments.map((seg) => {
            const band = bandByKey[seg.activity];
            // Encasement, culverts and the surface / services activities have no
            // band — each is drawn by its own block below.
            if (!band) return null;
            const x = xFor(seg.chainageStart);
            const w = Math.max(xFor(seg.chainageEnd) - x, 2);
            const colour = STATUS_COLOURS[seg.status] || STATUS_COLOURS.not_started;
            const hovered = hoveredSegmentId === seg.id;
            const active = activeSegmentId === seg.id;
            const isInProgress = seg.status === "in_progress";
            const isCompleted = seg.status === "completed";
            return (
              <g key={seg.id}>
                {/* Pulsing halo behind live workfronts */}
                {isInProgress && (
                  <rect
                    x={x - 2}
                    y={band.y - 1}
                    width={w + 4}
                    height={band.h + 2.5}
                    fill={colour}
                    filter="url(#vptGlow)"
                    pointerEvents="none"
                    style={{ animation: "vptPulse 1.9s ease-in-out infinite" }}
                  />
                )}
                <rect
                  x={x}
                  y={band.y + 1}
                  width={w}
                  height={band.h - 1.5}
                  fill={colour}
                  opacity={hovered || active ? 0.95 : 0.78}
                  stroke={active ? "#0f172a" : hovered ? "#0f172a" : "rgba(15,23,42,0.35)"}
                  strokeWidth={active ? 2.2 : hovered ? 1.6 : 0.8}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoveredSegmentId(seg.id)}
                  onMouseLeave={() => setHoveredSegmentId(null)}
                  onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                >
                  <title>{`${activityLabel(seg.activity)} · ${(seg.status || "").replace(/_/g, " ")}\n${formatChainage(seg.chainageStart)} → ${formatChainage(seg.chainageEnd)}`}</title>
                </rect>
                {/* Wet / dry material overlay — only excavation carries it */}
                {seg.activity === "excavation" && (seg.excavationCondition === "wet" || seg.excavationCondition === "dry") && (
                  <>
                    <rect
                      x={x}
                      y={band.y + 1}
                      width={w}
                      height={band.h - 1.5}
                      fill={seg.excavationCondition === "wet" ? "url(#vptWetHatch)" : "url(#vptDryHatch)"}
                      pointerEvents="none"
                    />
                    {/* Water-table line along the crown of a wet run */}
                    {seg.excavationCondition === "wet" && (
                      <line
                        x1={x} y1={band.y + 2.5}
                        x2={x + w} y2={band.y + 2.5}
                        stroke="#0369a1"
                        strokeWidth="1.8"
                        pointerEvents="none"
                      />
                    )}
                  </>
                )}
                {/* Light sweep across completed work */}
                {isCompleted && (
                  <rect
                    x={x}
                    y={band.y + 1}
                    width={w}
                    height={band.h - 1.5}
                    fill="url(#vptShimmer)"
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}

          </g>

          {/* One-shot chainage scan line — sweeps the route on load, then fades */}
          <g pointerEvents="none">
            <line
              x1={DX0} y1={BAND_TOP - 6}
              x2={DX0} y2={bandsBottom + 6}
              stroke="#38bdf8"
              strokeWidth="2.4"
              filter="url(#vptGlow)"
            >
              <animate attributeName="x1" values={`${DX0};${DX1}`} dur="1.4s" fill="freeze"
                calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
              <animate attributeName="x2" values={`${DX0};${DX1}`} dur="1.4s" fill="freeze"
                calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
              <animate attributeName="opacity" values="1;1;0" keyTimes="0;0.85;1" dur="1.8s" fill="freeze" />
            </line>
          </g>
        </g>

        {/* Trench wall (batter) lines */}
        <line x1={DX0} y1={BAND_TOP} x2={DX0 + BATTER} y2={bandsBottom} stroke="#7c6a48" strokeWidth="1.4" />
        <line x1={DX1} y1={BAND_TOP} x2={DX1 - BATTER} y2={bandsBottom} stroke="#7c6a48" strokeWidth="1.4" />

        {/* ── Structures lane ──
            Concrete encasement and culverts sit ON the pipe, but drawing them
            over the pipe-laying band buried both. Each now gets its own row
            under the trench, keeping its own identity (grey dashed for
            encasement, hatched teal for culverts) with a status strip along
            the top and a dashed tie line up to the pipe it belongs to. */}
        {structureRows.length > 0 && (
          <g>
            <rect
              x={DX0}
              y={STRUCTURES_TOP}
              width={DW}
              height={structuresLaneH}
              fill="#f8fafc"
              stroke="#e2e8f0"
              strokeWidth="1"
              rx="3"
            />

            {structureRows.map((row) => {
              const cy = structureRowY(row.key);
              return (
                <g key={`struct-row-${row.key}`}>
                  <line
                    x1={DX0} y1={cy} x2={DX1} y2={cy}
                    stroke={row.stroke} strokeWidth="1" strokeDasharray="2 4" opacity="0.3"
                  />
                  <text
                    x={LABEL_W}
                    y={cy + 4}
                    textAnchor="end"
                    fontSize="11"
                    fontWeight="700"
                    fill={selectedActivity === row.key ? "#b45309" : row.stroke}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                  >
                    {row.label}
                  </text>
                </g>
              );
            })}

            {structureSegments.map((seg) => {
              const row = STRUCTURE_ACTIVITIES.find((r) => r.key === seg.activity);
              if (!row) return null;
              const x = xFor(seg.chainageStart);
              const w = Math.max(xFor(seg.chainageEnd) - x, 3);
              const cy = structureRowY(row.key);
              const hovered = hoveredSegmentId === seg.id;
              const active = activeSegmentId === seg.id;
              const BAR_H = 13;
              const y = cy - BAR_H / 2;
              const strokeW = active ? 2.4 : hovered ? 2 : 1.4;
              return (
                <g
                  key={seg.id}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoveredSegmentId(seg.id)}
                  onMouseLeave={() => setHoveredSegmentId(null)}
                  onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                >
                  <title>{`${activityLabel(seg.activity)} · ${(seg.status || "").replace(/_/g, " ")}\n${formatChainage(seg.chainageStart)} → ${formatChainage(seg.chainageEnd)}`}</title>
                  {/* Tie line up to the pipe zone this structure sits on */}
                  <line
                    x1={x + w / 2} y1={y}
                    x2={x + w / 2} y2={pipeCY + pipeRY}
                    stroke={row.stroke}
                    strokeWidth={hovered || active ? 1.6 : 1}
                    strokeDasharray="3 3"
                    opacity={hovered || active ? 0.7 : 0.3}
                    pointerEvents="none"
                  />
                  <rect x={x} y={y} width={w} height={BAR_H} rx={row.rx} fill={row.fill} />
                  {row.hatch && (
                    <rect x={x} y={y} width={w} height={BAR_H} rx={row.rx} fill={row.hatch} />
                  )}
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={BAR_H}
                    rx={row.rx}
                    fill="none"
                    stroke={active || hovered ? "#0f172a" : row.stroke}
                    strokeWidth={strokeW}
                    strokeDasharray={row.dashed ? "5 3" : undefined}
                  />
                  {/* Status strip along the crown */}
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={3.2}
                    fill={STATUS_COLOURS[seg.status] || STATUS_COLOURS.not_started}
                    pointerEvents="none"
                  />
                </g>
              );
            })}
          </g>
        )}

        {/* ── Existing services lane ──
            One horizontal row per service type so bars stack above each other
            rather than overlapping. Adjoining services run as bars over their
            chainage range; crossing services are circles at their stake value,
            sized on the service diameter. */}
        {serviceRowKeys.length > 0 && (
          <g>
            <rect
              x={DX0}
              y={SERVICES_TOP}
              width={DW}
              height={servicesLaneH}
              fill="#f8fafc"
              stroke="#e2e8f0"
              strokeWidth="1"
              rx="3"
            />

            {serviceRowKeys.map((key) => {
              const meta = serviceMeta(key);
              const cy = serviceRowY(key);
              return (
                <g key={`svc-row-${key}`}>
                  <line
                    x1={DX0} y1={cy} x2={DX1} y2={cy}
                    stroke={meta.colour} strokeWidth="1" strokeDasharray="2 4" opacity="0.32"
                  />
                  <text
                    x={LABEL_W}
                    y={cy + 4}
                    textAnchor="end"
                    fontSize="11"
                    fontWeight="700"
                    fill={meta.colour}
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                  >
                    {meta.label}
                  </text>
                </g>
              );
            })}

            {/* Adjoining services — horizontal colour bars on their own row */}
            {serviceSegments
              .filter((seg) => seg.activity === "services_adjoining")
              .map((seg) => {
                const meta = serviceMeta(seg.serviceType || "");
                const cy = serviceRowY(meta.key);
                const x = xFor(seg.chainageStart);
                const w = Math.max(xFor(seg.chainageEnd) - x, 3);
                const hovered = hoveredSegmentId === seg.id;
                const active = activeSegmentId === seg.id;
                const BAR_H = 10;
                return (
                  <rect
                    key={seg.id}
                    x={x}
                    y={cy - BAR_H / 2}
                    width={w}
                    height={BAR_H}
                    rx={3}
                    fill={meta.colour}
                    opacity={hovered || active ? 1 : 0.85}
                    stroke={active || hovered ? "#0f172a" : "rgba(15,23,42,0.35)"}
                    strokeWidth={active ? 2 : hovered ? 1.5 : 0.8}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHoveredSegmentId(seg.id)}
                    onMouseLeave={() => setHoveredSegmentId(null)}
                    onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                  >
                    <title>
                      {`${meta.label} service — adjoining\n${formatChainage(seg.chainageStart)} → ${formatChainage(seg.chainageEnd)}` +
                        (seg.serviceDiameterMm ? `\nØ ${seg.serviceDiameterMm} mm` : "")}
                    </title>
                  </rect>
                );
              })}

            {/* Crossing services — circle at the stake value, sized on diameter */}
            {serviceSegments
              .filter((seg) => seg.activity === "services_crossing")
              .map((seg) => {
                const meta = serviceMeta(seg.serviceType || "");
                const cy = serviceRowY(meta.key);
                const cx = xFor(seg.chainageStart);
                const dia = Number(seg.serviceDiameterMm);
                const r = clamp(Number.isFinite(dia) && dia > 0 ? 4 + (dia / 1200) * 7 : 6, 4, 11);
                const hovered = hoveredSegmentId === seg.id;
                const active = activeSegmentId === seg.id;
                return (
                  <g
                    key={seg.id}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHoveredSegmentId(seg.id)}
                    onMouseLeave={() => setHoveredSegmentId(null)}
                    onClick={(e) => { e.stopPropagation(); onSegmentClick(seg); }}
                  >
                    <title>
                      {`${meta.label} service — crossing at ${formatChainage(seg.chainageStart)}` +
                        (Number.isFinite(dia) && dia > 0 ? `\nØ ${dia} mm` : "")}
                    </title>
                    {/* Crossing trace down through the trench */}
                    <line
                      x1={cx} y1={cy} x2={cx} y2={bandsBottom}
                      stroke={meta.colour}
                      strokeWidth={hovered || active ? 1.8 : 1.2}
                      strokeDasharray="4 3"
                      opacity={hovered || active ? 0.75 : 0.4}
                      pointerEvents="none"
                    />
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={meta.colour}
                      opacity={hovered || active ? 1 : 0.9}
                      stroke={active || hovered ? "#0f172a" : "rgba(15,23,42,0.45)"}
                      strokeWidth={active ? 2 : hovered ? 1.6 : 1}
                    />
                    <circle cx={cx - r * 0.3} cy={cy - r * 0.3} r={r * 0.28} fill="#ffffff" opacity="0.5" pointerEvents="none" />
                  </g>
                );
              })}
          </g>
        )}

        {/* Layer labels + dashed leader lines */}
        {bands.map((b) => {
          const cy = b.y + b.h / 2 + 4;
          return (
            <g key={`lbl-${b.key}`}>
              <text
                x={LABEL_W}
                y={cy}
                textAnchor="end"
                fontSize="13"
                fontWeight="700"
                fill={selectedActivity === b.key ? "#b45309" : "#1f2937"}
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {b.label}
              </text>
              <line
                x1={LABEL_W + 8}
                y1={cy - 4}
                x2={DX0 - 2}
                y2={cy - 4}
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
            </g>
          );
        })}

        {/* Test chevrons on the top surface */}
        {tests.map((t, i) => {
          const meta = TEST_TYPE_META[t.testType] || {};
          const rc = RESULT_COLORS[t._status] || RESULT_COLORS.pending;
          const cs = Number(t.chainageStart);
          const ce = Number(t.chainageEnd);
          let ch;
          if (Number.isFinite(cs) && Number.isFinite(ce) && ce > cs) ch = (cs + ce) / 2;
          else if (Number.isFinite(cs)) ch = cs;
          else ch = (assetStart + assetEnd) / 2; // asset-level (e.g. pressure)
          const x = xFor(ch);
          return (
            <g
              key={t.id || i}
              style={{ cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); onTestClick(t); }}
            >
              <title>{`${meta.icon || ""} ${meta.label || t.testType} — ${rc.label}`}</title>
              {/* hit area — stops short of the surface-works lane below */}
              <rect x={x - 10} y={CHEVRON_Y - 6} width="20" height={SURFACE_LANE_TOP - CHEVRON_Y + 6} fill="transparent" />
              {/* stem down to the top of the surface lane / road surface */}
              <line x1={x} y1={CHEVRON_Y + 9} x2={x} y2={SURFACE_Y - 8 - surfaceLaneH} stroke={rc.fill} strokeWidth="1.4" strokeDasharray="2 2" />
              {/* downward chevron */}
              <path
                d={`M ${x - 7} ${CHEVRON_Y} L ${x} ${CHEVRON_Y + 8} L ${x + 7} ${CHEVRON_Y}`}
                fill="none"
                stroke={rc.fill}
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={`M ${x - 7} ${CHEVRON_Y - 5} L ${x} ${CHEVRON_Y + 3} L ${x + 7} ${CHEVRON_Y - 5}`}
                fill="none"
                stroke={rc.fill}
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.55"
              />
            </g>
          );
        })}

        {/* Chainage axis */}
        <line x1={DX0} y1={AXIS_Y} x2={DX1} y2={AXIS_Y} stroke="#cbd5e1" strokeWidth="1" />
        {ticks.map((t) => (
          <g key={t.v}>
            <line
              x1={t.x}
              y1={AXIS_Y}
              x2={t.x}
              y2={AXIS_Y + (t.isMajor ? 10 : 5)}
              stroke={t.isMajor ? "#475569" : "#cbd5e1"}
              strokeWidth={t.isMajor ? 1.4 : 1}
            />
            {t.isMajor && (
              <text
                x={t.x}
                y={AXIS_Y + 24}
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fill="#334155"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {(t.v / 1000).toFixed(t.v % 1000 === 0 ? 1 : 2)}k
              </text>
            )}
          </g>
        ))}
        <text x={DX0} y={AXIS_Y + 24} textAnchor="start" fontSize="11" fontWeight="700" fill="#94a3b8" fontFamily="ui-sans-serif, system-ui, sans-serif">
          {formatChainage(assetStart)}
        </text>
        <text x={DX1} y={AXIS_Y + 24} textAnchor="end" fontSize="11" fontWeight="700" fill="#94a3b8" fontFamily="ui-sans-serif, system-ui, sans-serif">
          {formatChainage(assetEnd)}
        </text>
      </svg>

      {/* Floating dark tooltip for the hovered segment */}
      {(() => {
        const hSeg = hoveredSegmentId
          ? assetSegments.find((s) => s.id === hoveredSegmentId)
          : null;
        if (!hSeg) return null;
        const isService = isServiceActivity(hSeg.activity);
        const svc = isService ? serviceMeta(hSeg.serviceType || "") : null;
        const isCrossing = hSeg.activity === "services_crossing";
        const band = bandByKey[hSeg.activity];
        const cx = isCrossing
          ? xFor(hSeg.chainageStart)
          : (xFor(hSeg.chainageStart) + xFor(hSeg.chainageEnd)) / 2;
        const leftPct = (cx / W) * 100;
        const isSurface = isSurfaceActivity(hSeg.activity);
        const isTmp = isTmpActivity(hSeg.activity);
        const anchorY = isService ? serviceRowY(svc.key) - 14
          : isTmp ? tmpRowY - 12
          : isSurface ? surfaceRowY(surfaceRowKeyFor(hSeg)) - 12
          : band ? band.y - 8
          : hSeg.activity === "culverts" ? pipeCY - pipeRY - 14
          : null;
        const topPct = anchorY === null ? 8 : (anchorY / H) * 100;
        const colour = isService
          ? svc.colour
          : isTmp ? TMP_COLOURS.bar
          : STATUS_COLOURS[hSeg.status] || STATUS_COLOURS.not_started;
        const lengthM = Math.max(Number(hSeg.chainageEnd) - Number(hSeg.chainageStart), 0);
        const dia = Number(hSeg.serviceDiameterMm);
        const wetDry = hSeg.activity === "excavation" && hSeg.excavationCondition
          ? excavationConditionMeta(hSeg.excavationCondition)
          : null;
        const clearSub = hSeg.activity === "site_clearance" && hSeg.clearanceSubType
          ? clearanceSubTypeMeta(hSeg.clearanceSubType)
          : null;
        return (
          <div
            style={{
              position: "absolute",
              left: `${Math.min(Math.max(leftPct, 12), 88)}%`,
              top: `${Math.max(topPct, 2)}%`,
              transform: "translate(-50%, -100%)",
              background: "#0f172a",
              color: "#f8fafc",
              borderRadius: 10,
              padding: "9px 12px",
              fontSize: 12,
              fontFamily: "ui-sans-serif, system-ui, sans-serif",
              display: "grid",
              gap: 4,
              minWidth: 180,
              pointerEvents: "none",
              boxShadow: "0 12px 30px rgba(15,23,42,0.4)",
              zIndex: 20,
              animation: "vptTooltipIn 140ms ease-out",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 800 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: colour, flexShrink: 0 }} />
              {isService ? `${svc.label} service` : activityLabel(hSeg.activity)}
              {clearSub && (
                <span style={{ fontSize: 10, fontWeight: 700, color: "#cbd5e1" }}>
                  {clearSub.prefix} {clearSub.label}
                </span>
              )}
              <span style={{
                marginLeft: "auto", fontSize: 10, fontWeight: 700,
                color: colour, textTransform: "uppercase", letterSpacing: 0.4,
              }}>
                {isService
                  ? (isCrossing ? "crossing" : "adjoining")
                  : (hSeg.status || "").replace(/_/g, " ")}
              </span>
            </div>
            <div style={{ color: "#cbd5e1", fontWeight: 600 }}>
              {isCrossing ? (
                <>Stake {formatChainage(hSeg.chainageStart)}</>
              ) : (
                <>
                  {formatChainage(hSeg.chainageStart)} → {formatChainage(hSeg.chainageEnd)}
                  <span style={{ color: "#94a3b8" }}> · {lengthM.toLocaleString("en-ZA")} m</span>
                </>
              )}
              {Number.isFinite(dia) && dia > 0 && (
                <span style={{ color: "#94a3b8" }}> · Ø {dia} mm</span>
              )}
            </div>
            {wetDry && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: wetDry.key === "wet" ? "#7dd3fc" : "#fcd34d" }}>
                {wetDry.icon} {wetDry.label}
              </div>
            )}
            {hSeg.diaryNo && (
              <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>
                Diary #{hSeg.diaryNo} — click segment to feature
              </div>
            )}
          </div>
        );
      })()}
      </div>
      {/* Structures + test legend chips */}
      <div style={ts.foot}>
        <span style={ts.footItem}>
          <span style={{ ...ts.encChip, background: ENCASEMENT_FILL, borderColor: ENCASEMENT_STROKE }} />
          Concrete encasement — own row under the trench, tied to the pipe
        </span>
        <span style={ts.footItem}>
          <span style={{ ...ts.culvChip, background: CULVERT_FILL, borderColor: CULVERT_STROKE }} />
          Culvert — own row under the trench, tied to the pipe
        </span>
        <span style={ts.footItem}>
          <span style={ts.chevChip}>˅</span> Quality test location
        </span>
        {surfaceRowKeys.length > 0 && (
          <span style={ts.footItem}>
            <span style={ts.surfChip} />
            Surface works — above the road line, coloured by status
          </span>
        )}
        {tmpSegments.length > 0 && (
          <span style={ts.footItem}>
            <span style={{ ...ts.svcBarChip, background: TMP_COLOURS.bar, borderColor: TMP_COLOURS.stroke, marginRight: 0 }} />
            Traffic management — top lane, CH start → CH finish
          </span>
        )}
        {surfaceRowKeys.some((k) => k.startsWith("site_clearance:")) &&
          CLEARANCE_SUBTYPES.map((sub) => (
            <span key={`clr-${sub.key}`} style={ts.footItem}>
              <span style={{ ...ts.svcBarChip, background: sub.colour, marginRight: 0 }} />
              {sub.prefix} {sub.label} sub-section
            </span>
          ))}
        {assetSegments.some((x) => x.activity === "excavation" && x.excavationCondition) && (
          <>
            <span style={ts.footItem}>
              <span style={ts.wetChip} /> Wet excavation
            </span>
            <span style={ts.footItem}>
              <span style={ts.dryChip} /> Dry excavation
            </span>
          </>
        )}
      </div>

      {/* Services legend */}
      <div style={ts.svcLegend}>
        <div style={ts.svcLegendHead}>
          <strong style={ts.svcLegendTitle}>Existing services</strong>
          <span style={ts.svcLegendHint}>
            <span style={{ ...ts.svcBarChip, background: UNKNOWN_SERVICE.colour }} /> adjoining (chainage range)
            <span style={{ ...ts.svcDotChip, background: UNKNOWN_SERVICE.colour, marginLeft: 12 }} /> crossing (stake value, circle sized on diameter)
          </span>
        </div>
        <div style={ts.svcLegendRow}>
          {SERVICE_TYPES.map((s) => {
            const used = serviceRowKeys.includes(s.key);
            return (
              <span key={s.key} style={{ ...ts.svcChip, opacity: used ? 1 : 0.42 }}>
                <span style={{ ...ts.svcBarChip, background: s.colour }} />
                {s.label}
              </span>
            );
          })}
          {serviceRowKeys.includes(UNKNOWN_SERVICE.key) && (
            <span style={ts.svcChip}>
              <span style={{ ...ts.svcBarChip, background: UNKNOWN_SERVICE.colour }} />
              {UNKNOWN_SERVICE.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const ts = {
  card: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 18,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 },
  title: { margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a" },
  sub: { margin: "4px 0 0", fontSize: 12, color: "#64748b" },
  pillInfo: {
    fontSize: 12, fontWeight: 700, color: "#1d4ed8",
    background: "#eff6ff", border: "1px solid #bfdbfe",
    padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap",
  },
  pillActive: {
    fontSize: 12, fontWeight: 700, color: "#92400e",
    background: "#fef3c7", border: "1px solid #fcd34d",
    padding: "6px 12px", borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
  },
  pillX: { marginLeft: 6, opacity: 0.7 },

  segCard: {
    display: "block", width: "100%", textAlign: "left",
    background: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: 12, padding: "12px 16px", marginBottom: 14,
    boxShadow: "0 4px 12px rgba(15,23,42,0.05)",
  },
  segCardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 },
  segCardName: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 800, color: "#0f172a" },
  segDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  segCardStatus: {
    fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5,
    color: "#475569", background: "#f1f5f9", border: "1px solid #e2e8f0",
    padding: "3px 9px", borderRadius: 6,
  },
  segCardRow: { display: "flex", flexWrap: "wrap", gap: "6px 22px", fontSize: 13, color: "#334155", fontFamily: "ui-monospace, SFMono-Regular, monospace" },
  segCardLbl: { color: "#94a3b8", fontWeight: 700, marginRight: 6, fontFamily: "ui-sans-serif, system-ui, sans-serif", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 },
  segCardHint: { marginTop: 8, fontSize: 11, color: "#94a3b8", fontWeight: 600, textAlign: "center" },
  segCardActions: {
    marginTop: 11, paddingTop: 11, borderTop: "1px dashed #e2e8f0",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  measureBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8",
    borderRadius: 10, padding: "7px 14px", fontSize: 12, fontWeight: 800, cursor: "pointer",
  },
  measureBadge: {
    background: "#1d4ed8", color: "#ffffff", borderRadius: 999,
    padding: "1px 7px", fontSize: 10, fontWeight: 800,
  },
  measureHint: { fontSize: 11, color: "#94a3b8", fontWeight: 600 },
  diaryLink: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: 13,
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },

  svg: { width: "100%", height: "auto", display: "block" },

  foot: { display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10, fontSize: 12, color: "#475569" },
  footItem: { display: "flex", alignItems: "center", gap: 7 },
  encChip: { width: 18, height: 12, borderRadius: 3, border: "1.5px dashed", display: "inline-block" },
  culvChip: { width: 18, height: 12, border: "1.6px solid", display: "inline-block" },
  chevChip: { fontWeight: 900, color: "#0ea5e9", fontSize: 15, lineHeight: 1 },
  surfChip: {
    width: 18, height: 8, borderRadius: 2, display: "inline-block",
    background: "linear-gradient(90deg,#22c55e 0 45%,#f59e0b 45% 100%)",
    border: "1px solid rgba(15,23,42,0.35)",
  },
  wetChip: {
    width: 18, height: 10, borderRadius: 2, display: "inline-block",
    background: "repeating-linear-gradient(90deg,#0369a1 0 2px,#bae6fd 2px 5px)",
    border: "1px solid #0369a1", borderTopWidth: 2.5,
  },
  dryChip: {
    width: 18, height: 10, borderRadius: 2, display: "inline-block",
    background: "radial-gradient(circle at 4px 3px,#b45309 1px,transparent 1.5px), radial-gradient(circle at 11px 7px,#b45309 1px,transparent 1.5px), #fef3c7",
    border: "1px solid #b45309",
  },

  svcLegend: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px dashed #e2e8f0",
    display: "grid",
    gap: 8,
  },
  svcLegendHead: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  svcLegendTitle: {
    fontSize: 11, fontWeight: 800, textTransform: "uppercase",
    letterSpacing: 0.5, color: "#475569",
  },
  svcLegendHint: { display: "flex", alignItems: "center", fontSize: 11, color: "#94a3b8", fontWeight: 600, flexWrap: "wrap" },
  svcLegendRow: { display: "flex", gap: "8px 16px", flexWrap: "wrap" },
  svcChip: { display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#334155", fontWeight: 600 },
  svcBarChip: {
    width: 20, height: 9, borderRadius: 3, display: "inline-block",
    border: "1px solid rgba(15,23,42,0.35)", flexShrink: 0, marginRight: 4,
  },
  svcDotChip: {
    width: 12, height: 12, borderRadius: "50%", display: "inline-block",
    border: "1px solid rgba(15,23,42,0.45)", flexShrink: 0, marginRight: 4,
  },
};


/* ------------------------------------------------------------------ */
/* TestDetailPopup — opened by clicking a test marker. Rendered via      */
/* portal so it positions to the viewport regardless of any transform.   */
/* ------------------------------------------------------------------ */

function TestDetailPopup({ test, onClose }) {
  const meta = TEST_TYPE_META[test.testType] || {};
  const status = test._status || computeTestStatus(test);
  const rc = RESULT_COLORS[status];

  const Row = ({ label, value }) =>
    value === undefined || value === null || value === "" ? null : (
      <div style={pop.row}>
        <span style={pop.rowLabel}>{label}</span>
        <span style={pop.rowValue}>{value}</span>
      </div>
    );

  return createPortal(
    <div style={pop.overlay} onClick={onClose}>
      <div style={pop.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...pop.head, background: rc.soft, borderBottom: `1px solid ${rc.border}` }}>
          <div>
            <div style={pop.eyebrow}>{meta.icon} {meta.label} test</div>
            <h3 style={pop.title}>{test.title || testLocationLabel(test)}</h3>
          </div>
          <span style={{ ...pop.statusPill, background: rc.fill }}>{rc.label}</span>
        </div>

        <div style={pop.body}>
          <Row label="Location" value={testLocationLabel(test)} />
          <Row label="Date" value={test.testDate} />
          <Row label="Report no" value={test.reportNo} />
          <Row label="Lab / issuer" value={test.lab} />
          <Row label="Technician" value={test.technician} />

          {test.testType === "compaction" && (
            <>
              <Row label="Layer" value={test.compaction?.layer} />
              <Row label="Standard" value={test.compaction?.spec} />
              <Row label="Required" value={test.compaction?.requiredCompaction != null ? `${test.compaction.requiredCompaction}%` : null} />
              {(test.compaction?.positions || []).length > 0 && (
                <table style={pop.miniTable}>
                  <thead>
                    <tr>{["Position", "Dry ρ", "MDD", "Comp %"].map((h) => <th key={h} style={pop.miniTh}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {test.compaction.positions.map((p, i) => {
                      const v = positionRelativeCompaction(p);
                      const bad = v != null && test.compaction.requiredCompaction != null && v < Number(test.compaction.requiredCompaction);
                      return (
                        <tr key={i}>
                          <td style={pop.miniTd}>{p.ref || `#${i + 1}`}</td>
                          <td style={pop.miniTd}>{p.dryDensity ?? "—"}</td>
                          <td style={pop.miniTd}>{p.mdd ?? "—"}</td>
                          <td style={{ ...pop.miniTd, color: bad ? C.danger : C.success, fontWeight: 700 }}>{v != null ? v.toFixed(1) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {test.testType === "ndt" && (
            <>
              <Row label="Method" value={test.ndt?.method} />
              <Row label="Weld / joint" value={test.ndt?.weldNo} />
              <Row label="Welder" value={test.ndt?.welder} />
              <Row label="Pipe OD" value={test.ndt?.pipeOD} />
              <Row label="Acceptance" value={test.ndt?.acceptanceStandard} />
              <Row label="Process" value={test.ndt?.weldingProcess} />
              <Row label="Material" value={test.ndt?.materialSpec} />
              <Row label="Result" value={test.ndt?.result} />
              <Row label="Defects" value={test.ndt?.defects} />
            </>
          )}

          {test.testType === "cube" && (
            <>
              <Row label="Pour ref" value={test.cube?.pourRef} />
              <Row label="Element" value={test.cube?.element} />
              <Row label="Specified" value={test.cube?.specifiedStrength != null ? `${test.cube.specifiedStrength} MPa` : null} />
              <Row label="Date cast" value={test.cube?.dateCast} />
              {(() => {
                const avg = cubeAverage28(test.cube);
                return <Row label="28-day average" value={avg != null ? `${avg.toFixed(1)} MPa` : null} />;
              })()}
              {(test.cube?.cubes || []).length > 0 && (
                <table style={pop.miniTable}>
                  <thead>
                    <tr>{["Set", "ID", "Load", "MPa"].map((h) => <th key={h} style={pop.miniTh}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {test.cube.cubes.map((c, i) => (
                      <tr key={i}>
                        <td style={pop.miniTd}>{c.setLabel}d</td>
                        <td style={pop.miniTd}>{c.cubeId || "—"}</td>
                        <td style={pop.miniTd}>{c.loadKN ?? "—"}</td>
                        <td style={pop.miniTd}>{c.strengthMPa != null ? Number(c.strengthMPa).toFixed(1) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {test.testType === "pressure" && (
            <>
              <Row label="Medium" value={test.pressure?.medium} />
              <Row label="Test pressure" value={test.pressure?.testPressure != null ? `${test.pressure.testPressure} ${test.pressure.units || ""}` : null} />
              <Row label="Duration" value={test.pressure?.durationMinutes != null ? `${test.pressure.durationMinutes} min` : null} />
              <Row label="Allowable loss" value={test.pressure?.allowableLoss} />
              <Row label="Actual loss" value={test.pressure?.actualLoss} />
              <Row label="Disinfection date" value={test.pressure?.disinfectionDate} />
              <Row label="Disinfection method" value={test.pressure?.disinfectionMethod} />
              <Row label="Bacteriological" value={test.pressure?.bacteriologicalResult} />
              <Row label="Clearance date" value={test.pressure?.clearanceDate} />
            </>
          )}

          {test.documentRef && <Row label="Document" value={test.documentRef} />}
          {test.notes && <Row label="Notes" value={test.notes} />}
        </div>

        <div style={pop.foot}>
          <button style={pop.closeBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/* DiaryPopup — opened by clicking a segment's diary reference. Shows    */
/* the linked site-diary record in a portal modal.                       */
/* ------------------------------------------------------------------ */

function prettifyFlagKey(k) {
  return String(k).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function tickedFlags(obj) {
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj)
    .filter(([, v]) => v === true || v === "Yes" || v === "yes")
    .map(([k]) => prettifyFlagKey(k));
}

function DiaryPopup({ diary, onClose }) {
  const d = diary || {};
  const activities = Array.isArray(d.linkedProgrammeActivities) ? d.linkedProgrammeActivities : [];
  const labour = tickedFlags(d.labourFlags);
  const plant = tickedFlags(d.plantFlags);

  const Row = ({ label, value }) =>
    value === undefined || value === null || value === "" ? null : (
      <div style={pop.row}>
        <span style={pop.rowLabel}>{label}</span>
        <span style={pop.rowValue}>{value}</span>
      </div>
    );

  const progressRange =
    Number.isFinite(Number(d.progressChainageStart)) && Number.isFinite(Number(d.progressChainageEnd))
      ? `${formatChainage(d.progressChainageStart)} → ${formatChainage(d.progressChainageEnd)}`
      : null;

  return createPortal(
    <div style={pop.overlay} onClick={onClose}>
      <div style={pop.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...pop.head, background: "#eff6ff", borderBottom: "1px solid #bfdbfe" }}>
          <div>
            <div style={pop.eyebrow}>📓 Site Diary</div>
            <h3 style={pop.title}>{d.diaryNo || d.id || "Site Diary"}</h3>
          </div>
          {d.date && <span style={{ ...pop.statusPill, background: "#1d4ed8" }}>{d.date}</span>}
        </div>

        <div style={pop.body}>
          {d._missing && (
            <div style={{ ...pop.row, color: "#92400e", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px", margin: "4px 0 8px" }}>
              The full diary record wasn’t found — showing the details stored on the progress segment.
            </div>
          )}

          <Row label="Diary no" value={d.diaryNo} />
          <Row label="Date" value={d.date} />
          <Row label="Rainfall (mm)" value={d.rainfallMm} />
          <Row label="Works performed" value={d.worksPerformed} />
          <Row label="General comments" value={d.generalComments} />
          <Row label="Staff summary" value={d.staffSummary} />
          <Row label="Plant summary" value={d.plantSummary} />
          <Row label="Progress activity" value={d.progressActivity ? (ACTIVITY_LABELS[d.progressActivity] || d.progressActivity) : null} />
          <Row label="Progress status" value={d.progressStatus} />
          <Row label="Progress range" value={progressRange} />
          <Row label="Progress notes" value={d.progressNotes} />
          {labour.length > 0 && <Row label="Labour on site" value={labour.join(", ")} />}
          {plant.length > 0 && <Row label="Plant on site" value={plant.join(", ")} />}

          {activities.length > 0 && (
            <table style={pop.miniTable}>
              <thead>
                <tr>{["Activity", "Status", "Qty", "Note"].map((h) => <th key={h} style={pop.miniTh}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {activities.map((a, i) => (
                  <tr key={a.id || a.activityId || i}>
                    <td style={pop.miniTd}>{a.activityName || a.activityId || "—"}</td>
                    <td style={pop.miniTd}>{a.activityStatus || "—"}</td>
                    <td style={pop.miniTd}>
                      {a.diaryQuantity != null && a.diaryQuantity !== ""
                        ? `${a.diaryQuantity}${a.diaryQuantityUnit ? " " + a.diaryQuantityUnit : ""}`
                        : "—"}
                    </td>
                    <td style={pop.miniTd}>{a.activityNote || a.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Row
            label="Sign-off"
            value={
              d.contractorSigned || d.engineerSigned
                ? `Contractor: ${d.contractorSigned || "—"}${d.contractorSignName ? ` (${d.contractorSignName})` : ""} · Engineer: ${d.engineerSigned || "—"}${d.engineerSignName ? ` (${d.engineerSignName})` : ""}`
                : null
            }
          />
        </div>

        <div style={pop.foot}>
          <button style={pop.closeBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  page: { padding: 24, display: "grid", gap: 20, background: "#f8fafc", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" },
  headerRight: { display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" },
  title: { margin: 0, fontSize: 28, color: "#0f172a" },
  subtitle: { margin: "6px 0 0", color: "#64748b" },

  statCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: "16px 20px",
    minWidth: 200,
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gap: 2,
  },
  statLabel: { color: "#64748b", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  statValue: { fontSize: 32, color: "#0f172a", lineHeight: 1.1, fontWeight: 800 },
  statSub: { color: "#64748b", fontSize: 12 },

  presentationButton: {
    border: "none",
    borderRadius: 16,
    padding: "14px 22px",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
    color: "#ffffff",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 14,
    letterSpacing: 0.3,
    boxShadow: "0 12px 28px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 18, padding: 20, boxShadow: "0 10px 25px rgba(15, 23, 42, 0.05)" },
  cardTitle: { margin: "0 0 16px", fontSize: 18, color: "#0f172a" },

  setupGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 12, alignItems: "start", marginBottom: 12 },
  geoGrid: { display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 12, alignItems: "start", padding: 12, borderRadius: 12, background: "#f8fafc", border: "1px dashed #cbd5e1", marginBottom: 12 },
  setupActions: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },

  toolbar: { display: "grid", gridTemplateColumns: "repeat(3, minmax(220px, 1fr))", gap: 12, marginBottom: 18 },

  label: { display: "grid", gap: 6, color: "#334155", fontSize: 13, fontWeight: 600 },
  helperText: { color: "#64748b", fontSize: 11, fontWeight: 400, fontStyle: "italic" },
  input: { width: "100%", border: "1px solid #cbd5e1", borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none", background: "#ffffff", boxSizing: "border-box" },
  searchRow: { display: "flex", gap: 8 },

  primaryButton: { border: "none", borderRadius: 12, padding: "11px 18px", background: "#0f172a", color: "#ffffff", fontWeight: 700, cursor: "pointer" },
  secondaryButton: { border: "1px solid #cbd5e1", borderRadius: 10, padding: "10px 14px", background: "#f8fafc", color: "#0f172a", fontWeight: 700, cursor: "pointer" },

  /* polished asset summary */
  assetSummary: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "14px 18px",
    borderRadius: 14,
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    color: "#334155",
    marginBottom: 22,
    border: "1px solid #e5e7eb",
  },
  summaryLeft: { display: "flex", alignItems: "center", gap: 12 },
  summaryIcon: { fontSize: 28 },
  summaryName: { fontSize: 16, color: "#0f172a", display: "block", lineHeight: 1.2 },
  summaryType: { fontSize: 12, color: "#64748b", fontWeight: 500 },
  summaryRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, fontSize: 13 },
  summaryLength: { fontWeight: 700, color: "#0f172a" },
  routeBadgeOk: {
    fontSize: 11, fontWeight: 700,
    color: "#065f46",
    background: "#d1fae5",
    border: "1px solid #6ee7b7",
    padding: "3px 10px", borderRadius: 999,
  },
  routeBadgeMissing: {
    fontSize: 11, fontWeight: 700,
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    padding: "3px 10px", borderRadius: 999,
  },
  routeImportButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "6px 12px",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    marginTop: 4,
  },

  /* Polished pipe rendering */
  pipeWrapper: { display: "grid", gap: 8 },
  pipeShell: {
    background: "linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    padding: "32px 24px 16px",
    position: "relative",
  },
  pipeTrack: {
    position: "relative",
    height: 64,
    borderRadius: 999,
    cursor: "crosshair",
    margin: "0 8px",
  },
  pipeBase: {
    position: "absolute",
    inset: "16px 0",
    borderRadius: 999,
    background: "linear-gradient(180deg, #cbd5e1 0%, #94a3b8 45%, #64748b 100%)",
    boxShadow: "inset 0 4px 8px rgba(255,255,255,0.55), inset 0 -8px 14px rgba(15,23,42,0.30), 0 14px 22px rgba(15,23,42,0.15)",
  },
  pipeHighlight: {
    position: "absolute",
    top: 18,
    left: 0,
    right: 0,
    height: 6,
    borderRadius: 999,
    background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 100%)",
    pointerEvents: "none",
  },
  segment: {
    position: "absolute",
    top: 18,
    height: 28,
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    transition: "transform 120ms ease",
  },
  endCap: {
    position: "absolute",
    top: 14,
    width: 14,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(180deg, #94a3b8 0%, #475569 100%)",
    boxShadow: "inset 0 2px 4px rgba(255,255,255,0.5), 0 2px 6px rgba(0,0,0,0.2)",
  },
  ruler: { position: "relative", height: 18, marginTop: 6, marginLeft: 8, marginRight: 8 },
  tick: { position: "absolute", top: 0, width: 1, transform: "translateX(-0.5px)" },
  tickLabel: { position: "absolute", top: 13, left: "50%", transform: "translateX(-50%)", fontSize: 10, color: "#475569", fontWeight: 600, whiteSpace: "nowrap" },
  chainageLabels: { display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "#94a3b8", fontWeight: 600 },

  derivedValue: {
    border: "1px dashed #cbd5e1", borderRadius: 10, padding: "9px 12px",
    background: "#f8fafc", color: "#0f172a", fontWeight: 800, fontSize: 14,
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
  },

  kmzBlock: {
    marginTop: 14, padding: 14, borderRadius: 14,
    border: "1px solid #e2e8f0", background: "#f8fafc", display: "grid", gap: 10,
  },
  kmzHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" },
  kmzTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 },
  kmzRequired: {
    fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    color: "#b45309", background: "#fef3c7", border: "1px solid #fcd34d",
    borderRadius: 999, padding: "2px 8px",
  },
  kmzHint: { margin: "4px 0 0", fontSize: 12, color: "#64748b", maxWidth: 620, lineHeight: 1.5 },
  kmzActions: { display: "flex", gap: 8, flexShrink: 0 },
  kmzButton: {
    border: "1px solid #2563eb", background: "#2563eb", color: "#ffffff",
    borderRadius: 10, padding: "9px 16px", fontSize: 12, fontWeight: 800, cursor: "pointer",
  },
  kmzClear: {
    border: "1px solid #cbd5e1", background: "#ffffff", color: "#475569",
    borderRadius: 10, padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  kmzError: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    borderRadius: 10, padding: "9px 12px", fontSize: 12, fontWeight: 700, lineHeight: 1.5,
  },
  kmzSummary: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  kmzFileChip: {
    fontSize: 12, fontWeight: 700, color: "#166534", background: "#dcfce7",
    border: "1px solid #86efac", borderRadius: 999, padding: "5px 12px",
  },
  kmzStat: {
    display: "inline-flex", alignItems: "baseline", gap: 7,
    background: "#ffffff", border: "1px solid #e2e8f0",
    borderRadius: 999, padding: "5px 13px",
  },
  kmzStatLabel: { fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "#94a3b8" },
  kmzStatValue: { fontSize: 13, fontWeight: 800, color: "#0f172a", fontFamily: "ui-monospace, SFMono-Regular, monospace" },
  kmzWarn: { fontSize: 11, fontWeight: 700, color: "#b45309" },

  legend: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, color: "#475569", fontSize: 13, alignItems: "center" },
  legendChip: {
    display: "inline-flex", alignItems: "center", gap: 7,
    padding: "5px 12px", borderRadius: 999,
    background: "#ffffff", border: "1px solid #e2e8f0",
    fontSize: 12, fontWeight: 700, color: "#0f172a",
  },
  legendDot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  legendNote: { color: "#64748b", fontStyle: "italic" },

  empty: { padding: 18, borderRadius: 12, background: "#f8fafc", color: "#64748b" },

  selectedInfo: { marginTop: 0, color: "#334155" },
  diaryList: { display: "grid", gap: 12 },
  diaryCard: { border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, background: "#ffffff" },
  diaryTop: { display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 },
  badge: { background: "#eef2ff", color: "#3730a3", borderRadius: 999, padding: "4px 10px", fontSize: 12, fontWeight: 700 },
  diaryText: { margin: "4px 0", color: "#334155" },
  notes: { margin: "8px 0 0", color: "#475569", fontStyle: "italic" },
  diaryId: { margin: "8px 0 0", color: "#94a3b8", fontSize: 12 },

  /* ── Test overlay controls ── */
  testControls: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    padding: "10px 14px",
    borderRadius: 12,
    background: C.primarySoft,
    marginBottom: 14,
  },
  testToggle: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 13,
    fontWeight: 700,
    color: C.primary,
    cursor: "pointer",
  },
  miniSelect: {
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 13,
    background: "#fff",
    outline: "none",
  },
  testLegend: {
    display: "flex",
    gap: 12,
    marginLeft: "auto",
    fontSize: 12,
    color: C.inkMuted,
    alignItems: "center",
    flexWrap: "wrap",
  },

  /* ── Test marker lane (overlaid above the pipe) ── */
  markerLane: {
    position: "relative",
    height: 38,
    minWidth: 0,
    marginBottom: 4,
  },
  assetBand: {
    position: "absolute",
    left: "4%",
    right: "4%",
    height: 18,
    borderRadius: 6,
    border: "1.5px solid",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    padding: "0 8px",
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  rangeMarker: {
    position: "absolute",
    bottom: 2,
    height: 9,
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    opacity: 0.95,
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    padding: 0,
  },
  pointMarker: {
    position: "absolute",
    bottom: 0,
    transform: "translateX(-50%)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  pointDot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50% 50% 50% 0",
    transform: "rotate(-45deg)",
    color: "#fff",
    fontSize: 9,
    boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
  },

  /* ── Coverage ── */
  coverageControls: {
    display: "flex",
    gap: 16,
    alignItems: "flex-end",
    flexWrap: "wrap",
    marginBottom: 14,
  },
  coverageStat: {
    marginLeft: "auto",
    textAlign: "right",
    minWidth: 120,
  },
  coverageStatLabel: {
    display: "block",
    fontSize: 11,
    color: C.inkSubtle,
    textTransform: "capitalize",
  },
  coverageStatValue: {
    display: "block",
    fontSize: 26,
    fontWeight: 800,
    color: C.primary,
  },
  coverageStatSub: {
    fontSize: 11,
    color: C.inkMuted,
  },
  coverageBar: {
    position: "relative",
    height: 22,
    borderRadius: 6,
    background: "repeating-linear-gradient(45deg, #eef2f6, #eef2f6 6px, #e2e8f0 6px, #e2e8f0 12px)",
    overflow: "hidden",
    border: `1px solid ${C.border}`,
  },
  coverageFill: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: C.success,
    opacity: 0.85,
  },
  coverageGap: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: "repeating-linear-gradient(45deg, rgba(199,59,42,0.25), rgba(199,59,42,0.25) 5px, rgba(199,59,42,0.45) 5px, rgba(199,59,42,0.45) 10px)",
    borderLeft: `1px solid ${C.danger}`,
    borderRight: `1px solid ${C.danger}`,
  },
  coverageOk: {
    marginTop: 12,
    color: C.success,
    fontSize: 13,
    fontWeight: 600,
  },
  gapList: {
    marginTop: 12,
    display: "grid",
    gap: 6,
  },
  gapListTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: C.danger,
    marginBottom: 2,
  },
  gapRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    color: C.ink,
  },
  gapBadge: {
    display: "inline-block",
    minWidth: 56,
    textAlign: "center",
    padding: "2px 8px",
    borderRadius: 999,
    background: C.dangerSoft,
    color: C.danger,
    border: `1px solid ${C.dangerBorder}`,
    fontWeight: 700,
    fontSize: 12,
  },
};

const pop = {
  overlay: { position: "fixed", inset: 0, zIndex: tokens.z.modal, background: "rgba(14,17,22,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: tokens.font.body },
  dialog: { width: "min(560px, 100%)", maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", background: C.surface, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: tokens.shadow.lg, overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "16px 20px" },
  eyebrow: { fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.inkMuted },
  title: { margin: "4px 0 0", fontSize: 18, fontFamily: tokens.font.display, color: C.ink },
  statusPill: { color: "#fff", fontSize: 12, fontWeight: 800, padding: "5px 12px", borderRadius: 999 },
  body: { padding: "8px 20px 16px", overflowY: "auto", display: "grid", gap: 2 },
  row: { display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", borderBottom: `1px solid ${C.border}` },
  rowLabel: { fontSize: 12, color: C.inkSubtle, fontWeight: 600, flexShrink: 0 },
  rowValue: { fontSize: 13, color: C.ink, textAlign: "right", wordBreak: "break-word" },
  miniTable: { width: "100%", borderCollapse: "collapse", margin: "8px 0" },
  miniTh: { textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.inkSubtle, padding: "4px 6px", borderBottom: `1px solid ${C.border}` },
  miniTd: { fontSize: 12, color: C.ink, padding: "4px 6px", borderBottom: `1px solid ${C.border}` },
  foot: { padding: "12px 20px", borderTop: `1px solid ${C.border}`, background: C.surfaceAlt, display: "flex", justifyContent: "flex-end" },
  closeBtn: { border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: "9px 18px", background: C.surface, color: C.ink, fontWeight: 700, cursor: "pointer", fontSize: 13 },
};